import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { XMarkIcon, ChevronDownIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useProject, useProjects, useCreateProject, useUpdateProject, useCustomers, useCreateCustomer, useLabels } from '../../hooks/useProjects';
import { suggestProjectSlug } from '../../services/api/projects';
import { useProjectTemplates, useProjectTemplate } from '../../hooks/useProjectTemplates';
import { useAdminUsers } from '../../hooks/useAdmin';
import { useLead, useUpdateLead } from '../../hooks/useLeads';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import { PersistCurrencyAmountInput } from '../../components/common/CurrencyRateInput';
import { useAuth } from '../../services/authContext';
import { useTrackedTimeout } from '../../hooks/useTrackedTimeout';
import {
  useProjectStates,
  useCreateProjectState,
  useUpdateProjectState,
  useDeleteProjectState,
  useReorderProjectStates,
} from '../../hooks/useProjectStates';
import { StateListEditor } from '../../components/domain/StateListEditor';
import { ReapplyTemplateModal } from '../../components/domain/ReapplyTemplateModal';
import { Modal } from '../../components/common/Modal';
import { AppToast, type AppToastState } from '../../components/app-access/AppToast';
import { isStateInUseError } from '../../services/api/projectStates';
// Phase 41 Plan 41-10 (Surface 1) — template-trio selector + recommendation banner.
import {
  ProjectFormTemplateSelector,
  BASELINE_TEMPLATE_ID,
  type TemplateTrioAnchor,
  type TemplateTrioValue,
} from '../../components/projects/ProjectFormTemplateSelector';
import { TrioRecommendationBanner } from '../../components/projects/TrioRecommendationBanner';
// MKS-94 — billing terms card (own endpoint, own audience).
import { ProjectBillingTermsSection } from '../../components/projects/ProjectBillingTermsSection';
import { BillingScheduleSection } from '../../components/projects/BillingScheduleSection';
import { ProjectSharedLabelsSection } from '../../components/projects/ProjectSharedLabelsSection';
import {
  usePaymentTypes,
  useProjectTypes,
  useTaskTypes,
} from '../../hooks/useTemplateTypes';
import { useProjectRoles } from '../../hooks/useLeads';
import {
  SLA_BALICEK_PAYMENT_TYPE_ID,
  SLA_TASK_TYPE_ID,
  SLA_SUPPORT_PROJECT_TYPE_ID,
  FIXED_PRICE_PAYMENT_TYPE_ID,
  TIME_AND_MATERIAL_PAYMENT_TYPE_ID,
} from '../../constants/typeIds';
// Petra UAT 2026-05-26 — coverage entries UI removed from create form; the
// COVERAGE_TIMEZONES / COVERAGE_HOLIDAY_COUNTRIES / COVERAGE_PRESET_OPTIONS
// dropdowns are no longer rendered. Only CoverageEntryRow type is kept for
// the silent auto-seed (default Europe/Prague + business_hours + CZ).
import { type CoverageEntryRow } from '../../constants/coverage';
// 260913-jpy — the form seeds its two staleness day inputs from these
// constants rather than restating the numbers.
import { TASK_STALE_THRESHOLD_DAYS, TASK_GRAVEYARD_THRESHOLD_DAYS } from '../../utils/format';
import { DateField } from '../../components/ui/DateField';
// MKS-372 — the pre-conversion step inside the type-change confirmation.
import { useFlag } from '../../services/flagsContext';
import { TmConversionStep } from '../../components/domain/TmConversionStep';
// Phase 129 — the Engineering tab (cards container).
import { ProjectEngineeringTab } from '../../components/engineering/ProjectEngineeringTab';
import { useProjectEngineeringAccess } from '../../hooks/useProjectEngineering';
import type { TmReadinessIssue } from '../../services/api/tmConversion';
import { labelChipStyle } from '../../utils/labelColor';
// MKS-550 — the Billable / Non-billable labels are a projection of the Billable
// checkbox below, so the picker neither offers nor removes them.
import {
  billabilityLabelNameFor,
  isBillabilityLabelName,
} from '../../constants/billabilityLabels';

// Phase 129 (D-19) — the edit page's tabs. `?tab=` selects one; anything else shows General.
type ProjectFormTab = 'general' | 'engineering';

const PROJECT_FORM_TABS: Array<{ id: ProjectFormTab; label: string }> = [
  { id: 'general', label: 'General' },
  { id: 'engineering', label: 'Engineering' },
];

function isProjectFormTab(value: string | null): value is ProjectFormTab {
  return PROJECT_FORM_TABS.some((t) => t.id === value);
}
// quick 261103-rwl — T&M rates are optional when the project is non-billable.
import { isTmMissingRatesError, isTmRateRequired, tmOverageRatesPatch } from './tmOverageRates';

// Today + 1 year, ISO-formatted — sensible contractEnd default for UAT.
function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}
function isoTodayPlusYears(years: number): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() + years);
  return d.toISOString().slice(0, 10);
}

// UAT 2026-05-13 — overage rate row for SLA Balíček when overLimitAllowed=ON.
// roleId is stored as string to integrate with `<select>`; converted to int on submit.
interface OverageRateRow {
  roleId: string;
  hourlyRate: string;
}

// UAT 2026-05-13 — SLA Task priority tier form row. Hours / CSV inputs are
// converted to the backend-required minutes / number[] shape at submit.
interface SlaTierFormRow {
  name: string;
  orderIndex: number;
  responseHours: string;
  resolutionHours: string;
  responseWarningCsv: string;
  resolutionWarningCsv: string;
}

const DEFAULT_SLA_TIERS: SlaTierFormRow[] = [
  { name: 'P1 — Critical', orderIndex: 1, responseHours: '1', resolutionHours: '4', responseWarningCsv: '50, 80', resolutionWarningCsv: '50, 80' },
  { name: 'P2 — High', orderIndex: 2, responseHours: '4', resolutionHours: '24', responseWarningCsv: '50, 80', resolutionWarningCsv: '50, 80' },
  { name: 'P3 — Normal', orderIndex: 3, responseHours: '24', resolutionHours: '72', responseWarningCsv: '50, 80', resolutionWarningCsv: '50, 80' },
];
// MKS-211 (D-11) — the Default payment type id. Previously hardcoded here as
// its own copy of ProjectFormTemplateSelector's BASELINE_TEMPLATE_ID (both
// mirror backend/src/templates/paymentTypes.ts row id=1 /
// DEFAULT_PAYMENT_TYPE_ID); now imported directly so there is one constant,
// not two copies of the same literal.
const DEFAULT_PAYMENT_TYPE_ID = BASELINE_TEMPLATE_ID;

/**
 * MKS-263 — narrows a lead's `dealValueCurrency` (a plain `string` on the API
 * type) to the three currencies `moneyTotalCurrency` persists. Anything else
 * leaves the budget currency on its CZK default rather than writing a value
 * the backend enum would reject.
 */
function isPersistCurrency(value: string | null | undefined): value is 'CZK' | 'EUR' | 'USD' {
  return value === 'CZK' || value === 'EUR' || value === 'USD';
}

/**
 * MKS-205 — consumption-alert thresholds for the payment types that measure
 * consumption in money against a budget (Fixed Price, Time & Material).
 *
 * Rendered inside those two settings blocks so the config is reachable for
 * them, mirroring the SLA balíček "Alert thresholds (%)" input. When the
 * project has neither a contract amount nor a money budget there is nothing
 * to measure a percentage against, so the input is replaced by an explanation
 * (the ticket's explicit requirement).
 *
 * MKS-263 — `emptyHint` lets each block name the field the user has to fill;
 * the T&M block now carries that field itself ("Estimated maximum" above the
 * alerts) instead of pointing at the Default Budget on another template.
 */
export function BudgetAlertThresholdsField({
  value,
  onChange,
  hasBudget,
  inputId,
  textClass,
  emptyHint = 'Not available until this project has a budget. Set a contract amount (or a Default Budget above) and consumption alerts can be configured here.',
}: {
  value: string;
  onChange: (next: string) => void;
  hasBudget: boolean;
  inputId: string;
  textClass: string;
  emptyHint?: string;
}) {
  if (!hasBudget) {
    return (
      <div>
        <p className={`text-xs font-medium ${textClass}`}>Consumption alerts</p>
        <p className={`text-xs ${textClass} mt-0.5`}>{emptyHint}</p>
      </div>
    );
  }
  return (
    <div>
      <label htmlFor={inputId} className={`block text-xs font-medium ${textClass} mb-1`}>
        Alert thresholds (%)
      </label>
      <input
        id={inputId}
        type="text"
        inputMode="numeric"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        placeholder="80, 100"
      />
      <p className={`text-xs ${textClass} mt-1`}>
        Comma-separated percentages (1–200) of the budget booked so far. Each one notifies
        once for the life of the project. Leave blank for no alerts.
      </p>
    </div>
  );
}

export default function ProjectFormPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEditing = !!id;
  const projectId = id ? parseInt(id, 10) : undefined;
  const prefilledParentId = searchParams.get('parentId');
  const fromLeadId = searchParams.get('fromLead') ? parseInt(searchParams.get('fromLead')!, 10) : null;
  // Quick 260804-keu — which lead slot the created project fills: the billable
  // (converted) project by default, or the non-billable one when the modal's
  // non-billable "full form" link is used (?leadRole=nonBillable).
  const leadRole = searchParams.get('leadRole') === 'nonBillable' ? 'nonBillable' : 'billable';
  // Cancel returns to the lead's Set up projects modal when we came from it
  // (the ?setupProjects deep-link reopens it); plain history-back otherwise.
  const handleCancel = () =>
    fromLeadId ? navigate(`/sales/leads?setupProjects=${fromLeadId}`) : navigate(-1);

  const { data: project, isLoading: projectLoading } = useProject(projectId);
  const { data: leadData } = useLead(fromLeadId);
  const updateLeadMutation = useUpdateLead();
  const parentIdNum = prefilledParentId ? parseInt(prefilledParentId, 10) : undefined;
  const { data: parentProject } = useProject(parentIdNum);
  const { data: customers } = useCustomers();
  const { data: allProjects } = useProjects();
  const { data: allLabels } = useLabels();
  const createMutation = useCreateProject();
  // MKS-550 — with the flag on, the two billability labels leave the picker: they are
  // derived from the Billable checkbox and shown as a read-only badge instead. The
  // backend drops them from an update body either way, so an in-flight tab cannot
  // change them by saving.
  const billabilityLabelsReadOnly = useFlag('mks_550_billability_labels');
  const pickableLabels = useMemo(
    () =>
      billabilityLabelsReadOnly
        ? (allLabels ?? []).filter((label) => !isBillabilityLabelName(label.name))
        : (allLabels ?? []),
    [allLabels, billabilityLabelsReadOnly],
  );

  const { user: currentUser, isAdmin, isManager, isFinance } = useAuth();
  // MKS-94 — billing terms. Editable by admin, finance, or the project's own
  // PM; everyone else who can reach this page sees them read-only. The backend
  // repeats the check on PATCH /projects/:id/billing-terms.
  // MKS-211 — create form narrowing. A new project can never be created as
  // Default: the narrowed Payment dropdown + T&M seed apply only on the
  // CREATE path, unconditionally; an existing Default-type project still
  // edits correctly, every active payment type included. MKS-251 brings the
  // SLA balíček retainer and its package config into this form directly.
  const createModeNarrowingActive = !isEditing;
  const isProjectPM = !!project && project.projectManager?.id === currentUser?.id;
  const canEditBillingTerms = isAdmin || isFinance || isProjectPM;
  // MKS-251 — payment / project / task type are normal form fields now. On
  // create everyone who can reach the form picks them (unchanged). On edit the
  // audience is the same as billing terms — admin, finance, or the project's
  // own PM — and the backend repeats the check on PATCH /projects/:id.
  const canEditTypes = !isEditing || canEditBillingTerms;
  // MKS-264 — the Project type dropdown is replaced by a single "SLA Support
  // project" checkbox in the flags list. The only real choice the dropdown
  // offered was Default vs. SLA Support, and sitting next to the Payment
  // template it read as the setting that drives invoicing, which it is not.
  // MKS-251 — the create-mode payment allow-list. MKS-211's rule is that a new
  // project cannot be created as *Default*, and that rule stays. It narrowed
  // create to T&M + Fixed Price only because the SLA balíček retainer had no
  // home on this form yet (see its comment above, which names MKS-251 as the
  // ticket that gives it one). It now has one — the inline balíček block below
  // — and MKS-251's acceptance criterion is that a new project can be set up
  // as SLA balíček without any modal, so the retainer has its home on this
  // form and is on the create list. Default is still absent from the list.
  // T&M stays FIRST: the selector falls back to `allowedPaymentTypeIds[0]`
  // when paymentTypeId is still null, so this keeps MKS-211's T&M seed as the
  // landing value. The dropdown's own order comes from the API list, not from
  // this array.
  const createPaymentTypeIds = [
    TIME_AND_MATERIAL_PAYMENT_TYPE_ID,
    FIXED_PRICE_PAYMENT_TYPE_ID,
    SLA_BALICEK_PAYMENT_TYPE_ID,
  ];
  // A finance user who is neither admin nor this project's PM cannot save
  // anything else on this form (PATCH /projects/:id is manager+admin), so the
  // page collapses to the billing-terms card for them rather than showing a
  // form whose Save can only fail.
  const billingTermsOnly = isEditing && isFinance && !isAdmin && !isProjectPM;

  // Phase 129 (D-19..D-22) — General | Engineering tabs on the edit page. On /projects/new
  // nothing below changes the render.
  const tabParam = searchParams.get('tab');
  const tab: ProjectFormTab = isProjectFormTab(tabParam) ? tabParam : 'general';
  // D-05 — who sees the whole General form: admin, manager, finance (billing terms only, as
  // today) or the project's PM. Anyone else on the team gets the Engineering content alone, and
  // the page must not request admin-only data for them.
  const generalCapableViewer = isAdmin || isManager || isFinance || isProjectPM;
  const { data: users, isLoading: usersLoading } = useAdminUsers(
    false,
    !isEditing || generalCapableViewer,
  );
  // Membership comes from the server (T-129-18), not a client-side list. Admin and the PM are
  // members by definition, so they skip the probe.
  const engineeringAccess = useProjectEngineeringAccess(
    projectId,
    isEditing && !isAdmin && !isProjectPM,
  );
  const isTeamMember = isAdmin || isProjectPM || engineeringAccess.data?.member === true;
  const showEngineering = isEditing && isTeamMember;
  const engineeringOnly = showEngineering && !generalCapableViewer;
  // D-20 — each side keeps its unsaved edits across a switch: the Engineering panel is mounted on
  // first visit and then only hidden; General is always mounted and only hidden.
  const [engineeringVisited, setEngineeringVisited] = useState(false);
  const engineeringMounted = showEngineering && projectId !== undefined && (engineeringVisited || tab === 'engineering');
  const setTab = (next: ProjectFormTab): void => {
    if (next === 'engineering' || tab === 'engineering') setEngineeringVisited(true);
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    // replace: switching tabs must not stack history entries.
    setSearchParams(params, { replace: true });
  };
  const projectTabBar = (
    <div className="flex gap-2 border-b border-gray-200" role="tablist" aria-label="Project sections">
      {PROJECT_FORM_TABS.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={tab === t.id}
          onClick={() => setTab(t.id)}
          className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
            tab === t.id
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
  // F2-012: tracked timeout for the 200ms post-scroll focus call so a fast
  // navigate-away after a 409 slug-collision doesn't fire focus() on a
  // detached element. Mirror of cycle-1 F20-002 / F22-003.
  const { schedule: scheduleFocus } = useTrackedTimeout();

  // Phase 20 D-06: predicate matches Phase 19 D-22 — admin OR project's PM.
  const canEditStates =
    isEditing && !!projectId && (isAdmin || project?.projectManager?.id === currentUser?.id);

  // BL-03: useProjectTemplates and useProjectTemplate hit admin-only endpoints
  // (`requireAdmin` middleware in projectTemplateAdminRoutes). Although today's
  // route guard for /projects/:id/edit is `<AdminRoute>`, the page documents
  // an admin-OR-PM contract (canEditStates above). Gate the hooks on admin
  // explicitly so non-admin PMs (if the route ever loosens) don't emit 403s
  // on every page load.
  const { data: templates } = useProjectTemplates(isAdmin);
  const { data: projectStates } = useProjectStates(canEditStates ? projectId : undefined);
  const { data: appliedTemplate } = useProjectTemplate(
    isAdmin ? (project?.appliedTemplateId ?? undefined) : undefined
  );

  const createStateMutation = useCreateProjectState(projectId ?? 0);
  const updateStateMutation = useUpdateProjectState(projectId ?? 0);
  const deleteStateMutation = useDeleteProjectState(projectId ?? 0);
  const reorderStatesMutation = useReorderProjectStates(projectId ?? 0);

  const [taskStatesToast, setTaskStatesToast] = useState<AppToastState | null>(null);
  const [deleteConflict, setDeleteConflict] = useState<{
    stateId: number;
    stateName: string;
    taskCount: number;
  } | null>(null);
  const [reapplyDropdownOpen, setReapplyDropdownOpen] = useState(false);
  const [reapplyTargetTemplateId, setReapplyTargetTemplateId] = useState<number | null>(null);
  const reapplyDropdownRef = useRef<HTMLDivElement>(null);

  // 260727-czj — opt-in allowlist of ProjectRole ids permitted to move
  // tasks into in-progress columns. Seeded from the project row (below);
  // each checkbox toggle persists immediately via updateMutation, mirroring
  // the other Task States settings that save on interaction rather than on
  // the page-level Save button.
  const [inProgressAllowedRoleIds, setInProgressAllowedRoleIds] = useState<number[]>([]);

  // Click-outside for the Re-apply template dropdown (mirrors customer dropdown pattern).
  useEffect(() => {
    if (!reapplyDropdownOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (
        reapplyDropdownRef.current &&
        !reapplyDropdownRef.current.contains(e.target as Node)
      ) {
        setReapplyDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [reapplyDropdownOpen]);

  // Active templates for the Re-apply picker (already filters is_legacy via getProjectTemplates).
  const activeTemplates = useMemo(
    () => (templates ?? []).filter((t) => !t.isLegacy),
    [templates]
  );
  const reapplyChosenTemplate = useMemo(
    () =>
      reapplyTargetTemplateId != null
        ? activeTemplates.find((t) => t.id === reapplyTargetTemplateId) ?? null
        : null,
    [activeTemplates, reapplyTargetTemplateId]
  );
  const updateMutation = useUpdateProject();
  const createCustomerMutation = useCreateCustomer();

  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
  const [customerFilter, setCustomerFilter] = useState('');
  const customerDropdownRef = useRef<HTMLDivElement>(null);
  const customerInputRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState({
    templateId: '', // Phase 19 D-01..D-06; create path only
    name: '',
    slug: '', // Phase 27 D-13: optional 3-char slug; blank → server auto-derives
    fullName: '',
    description: '',
    parentId: prefilledParentId || '',
    customerId: '',
    projectManagerId: '',
    startDate: '',
    endDate: '',
    defaultRate: '',
    hoursTotal: '',
    moneyTotal: '',
    // Phase 72 (currency-aware budgets) — the Default money budget's
    // currency, persisted (not converted-and-discarded) alongside moneyTotal.
    moneyTotalCurrency: 'CZK' as 'CZK' | 'EUR' | 'USD',
    isDefault: false,
    mandatoryTasks: false,
    allowExternalIntake: false,
    // 260727-gob — open-board flag. MKS-477: new projects default to open (true);
    // the checkbox lets a PM opt out. Edit hydration reflects the stored value.
    openToAllInternal: true,
    // Phase 62 (Plan 62-06, TBI-01, D-01) — explicit billable flag; defaults
    // true for new projects so they appear in the To Be Invoiced queue.
    billable: true,
    // F3M-01 — disable hour reporting; defaults false for new leaf projects.
    // Parents get auto-flipped server-side on subproject creation / via migration.
    timeReportingDisabled: false,
    // 260913-jpy — per-project staleness indicator opt-in; defaults OFF for
    // new projects so no board shows the emoji until someone opts in. The
    // two day-count strings are seeded from the global constants (never
    // restated as literals) and only read when the toggle is on.
    stalenessIndicatorEnabled: false,
    stalenessStaleDays: String(TASK_STALE_THRESHOLD_DAYS),
    stalenessGraveyardDays: String(TASK_GRAVEYARD_THRESHOLD_DAYS),
    // Phase 37 (Plan 37-08) — SLA balíček satellite fields, sent only when
    // paymentTypeId === SLA_BALICEK_PAYMENT_TYPE_ID. Default currency EUR per UAT 2026-05-13.
    balicekHours: '',
    balicekAmount: '',
    balicekCurrency: 'EUR',
    // UAT 2026-05-13: optional SLA Balíček behavior — over-limit policy
    // and consumption-alert thresholds entered as comma-separated %.
    overLimitAllowed: false,
    alertThresholds: '',
    // Phase 53 (Plan 53-04 / COVERAGE-08) — rollover policy + cap for SLA Balíček.
    rolloverPolicy: 'none' as 'none' | 'monthly' | 'annual',
    rolloverCapHours: '',
    // UAT 2026-05-13: SLA Support contract — required when ProjectType=SLA Support.
    contractStart: '',
    contractEnd: '',
    contractFrequency: 'monthly',
    // Phase 72 Plan 06 (CUR-09, D-07) — single project-level T&M billing
    // currency. Per-role rates below are entered in this currency and
    // converted to CZK at invoice-generate time. Defaults to CZK.
    tmBillingCurrency: 'CZK' as 'CZK' | 'EUR' | 'USD',
  });
  const [selectedLabelIds, setSelectedLabelIds] = useState<number[]>([]);
  // UAT 2026-05-13 — SLA Task priority tiers. Backend gate requires ≥1 tier
  // when taskTypeId === SLA_TASK_TYPE_ID. Auto-seeded with 3 sensible
  // defaults when the user picks SLA Task; admin can add / remove / edit.
  const [slaTiers, setSlaTiers] = useState<SlaTierFormRow[]>([]);
  // UAT 2026-05-13 — per-role overage rates for SLA Balíček over-limit.
  // Backend gate (PAY-03) requires ≥1 entry when overLimitAllowed=true.
  const [overageRates, setOverageRates] = useState<OverageRateRow[]>([]);
  // Phase 58 (PAY-FIX-02, D-03) — Fixed Price contract fields.
  // Separate from balicekAmount/balicekCurrency — backend persists to distinct
  // contract_amount / contract_currency columns.
  const [contractAmount, setContractAmount] = useState<string>('');
  const [contractCurrency, setContractCurrency] = useState<string>('');
  const { data: projectRolesData } = useProjectRoles();
  const projectRoles = projectRolesData?.data ?? [];
  // UAT 2026-05-13 — coverage entries for SLA Support. Backend gate requires
  // ≥1 entry when ProjectType=SLA Support. Auto-seeded with one Europe/Prague
  // + business hours + CZ row when user lands on SLA Support (whether by
  // manual ProjectType pick or by auto-sync from Payment=SLA Balíček).
  const [coverageEntries, setCoverageEntries] = useState<CoverageEntryRow[]>([]);

  // Phase 41 Plan 41-10 (Surface 1) — template trio state (Payment / Project type / Task).
  const [trio, setTrio] = useState<TemplateTrioValue>({
    paymentTypeId: null,
    projectTypeId: null,
    taskTypeId: null,
  });
  const [lastAnchor, setLastAnchor] = useState<TemplateTrioAnchor | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // MKS-211 — a one-shot seed so a fresh create can never submit Default:
  // the first render where createModeNarrowingActive is true sets
  // trio.paymentTypeId to Time & Material (not Default), but only once, and
  // only while the user has not already picked something themselves.
  // MKS-261 — set the moment the user touches the Payment selector, so the
  // lead prefill below (which waits on an async query) can tell its own seed
  // apart from a choice somebody has already made by hand.
  const paymentTypePickedRef = useRef(false);
  const seededCreateDefaultRef = useRef(false);
  useEffect(() => {
    if (createModeNarrowingActive && !seededCreateDefaultRef.current) {
      seededCreateDefaultRef.current = true;
      setTrio((prev) =>
        prev.paymentTypeId === null
          ? { ...prev, paymentTypeId: TIME_AND_MATERIAL_PAYMENT_TYPE_ID }
          : prev,
      );
    }
  }, [createModeNarrowingActive]);

  // MKS-264 — a checkbox has no "unset" state, but `trio.projectTypeId` does:
  // left alone on create it stays null and the create payload sends that null
  // through, while the dropdown it replaces only *displayed* Default. So a
  // fresh create seeds the baseline id once, and the checkbox writes 1 / 2
  // explicitly from then on. Its own ref (not the payment seed's) so the two
  // seeds stay independent. The null guard keeps it from overwriting a value
  // the payment auto-sync below already wrote.
  const seededCreateProjectTypeRef = useRef(false);
  useEffect(() => {
    if (isEditing) return;
    if (seededCreateProjectTypeRef.current) return;
    seededCreateProjectTypeRef.current = true;
    setTrio((prev) =>
      prev.projectTypeId === null ? { ...prev, projectTypeId: BASELINE_TEMPLATE_ID } : prev,
    );
  }, [isEditing]);

  // MKS-205 — consumption alerts on Fixed Price / T&M. The config is reachable
  // only once the project has something to measure a percentage against: a
  // contract amount, or the Default Budget above.
  const hasBudgetForAlerts =
    contractAmount.trim() !== '' || form.moneyTotal.trim() !== '';
  // MKS-251 — the TPL-04 "locked after activity" guard is gone, on both sides.
  // Hours and invoices no longer pin a project to its templates; the backend
  // accepts the change and the confirmation below explains what it affects.
  // The only remaining reason to disable the dropdowns is authorization.
  const typeSelectorDisabled = !canEditTypes;
  const typeSelectorDisabledReason = !canEditTypes
    ? 'Only an admin, finance, or this project’s manager can change the types.'
    : undefined;

  const [error, setError] = useState<string | null>(null);
  const [slugError, setSlugError] = useState<string | null>(null); // Phase 27 D-13: 409 field error
  // UAT 2026-05-13 — Suggest-slug pending flag (disables the button while
  // the lookup is in flight, so multiple rapid clicks don't fan out).
  const [suggestingSlug, setSuggestingSlug] = useState(false);
  // UAT 2026-05-13 — inline error surface for SLA Balíček validation gates
  // (PAY-03 overage rate, PAY-10 alert threshold) so the user sees the
  // hint next to the offending field instead of the generic top-of-form box.
  const [balicekError, setBalicekError] = useState<string | null>(null);
  // MKS-251 — non-null while the "you are changing the project's types"
  // confirmation is open; holds one human-readable line per changed type.
  const [pendingTypeChange, setPendingTypeChange] = useState<string[] | null>(null);
  // MKS-372 — the pre-conversion check. `tmConversionOn` off keeps today's
  // behaviour exactly: no readiness query, no step in the confirmation, and
  // the backend's single "at least one per-role rate" gate as the only guard.
  const tmConversionOn = useFlag('mks_372_tm_conversion_check');
  // Null until the readiness query answers; the Save button only waits on a
  // definite `false`, so a failed or flag-off check never blocks the save.
  const [tmConversionReady, setTmConversionReady] = useState<boolean | null>(null);
  // The items the BACKEND refused with (code `tm_conversion_not_ready`) —
  // the belt to the dialog's braces, for a project that went stale between
  // the check and the save.
  const [tmConversionIssues, setTmConversionIssues] = useState<TmReadinessIssue[] | null>(
    null,
  );

  useEffect(() => {
    if (project && isEditing) {
      setForm({
        templateId: '',
        name: project.name || '',
        slug: project.slug || '', // Phase 27 D-14: read-only on edit
        fullName: project.fullName || '',
        description: project.description || '',
        parentId: project.parent?.id?.toString() || '',
        customerId: project.customer?.id?.toString() || '',
        projectManagerId: project.projectManager?.id?.toString() || '',
        startDate: project.startDate || '',
        endDate: project.endDate || '',
        defaultRate: project.defaultRate?.toString() || '',
        hoursTotal: project.hoursTotal?.toString() || '',
        moneyTotal: project.moneyTotal?.toString() || '',
        // Phase 72 (currency-aware budgets) — hydrate the persisted currency;
        // legacy rows default to 'CZK' server-side (72-01).
        moneyTotalCurrency: (project.moneyTotalCurrency as 'CZK' | 'EUR' | 'USD') ?? 'CZK',
        isDefault: project.isDefault || false,
        mandatoryTasks: project.mandatoryTasks || false,
        allowExternalIntake: project.allowExternalIntake || false,
        // 260727-gob — hydrate the open-board flag from the persisted value.
        openToAllInternal: project.openToAllInternal || false,
        // Phase 62 (Plan 62-06, TBI-01) — hydrate billable flag; backend
        // defaults true so `?? true` preserves the most common state.
        billable: project.billable ?? true,
        // F3M-01 — hydrate timeReportingDisabled from persisted project value.
        timeReportingDisabled: project.timeReportingDisabled ?? false,
        // 260913-jpy — hydrate the staleness indicator config from the
        // persisted project value, falling back to the global constants so
        // reopening the form never shows blank number inputs.
        stalenessIndicatorEnabled: project.stalenessIndicatorEnabled ?? false,
        stalenessStaleDays:
          project.stalenessStaleDays != null
            ? String(project.stalenessStaleDays)
            : String(TASK_STALE_THRESHOLD_DAYS),
        stalenessGraveyardDays:
          project.stalenessGraveyardDays != null
            ? String(project.stalenessGraveyardDays)
            : String(TASK_GRAVEYARD_THRESHOLD_DAYS),
        // UAT 2026-05-13 F-D6/Issue-B — hydrate balíček config from project
        // columns (surfaced on detail response by F-D4/F-D6 schema updates).
        // alertThresholds NOT yet surfaced on detail response → blank on
        // edit; user retypes if they want to change them. Tracked as a
        // known gap in batch findings.
        balicekHours: project.balicekHours != null ? String(project.balicekHours) : '',
        balicekAmount: project.balicekAmount ?? '',
        balicekCurrency:
          (project.balicekCurrency as 'CZK' | 'EUR' | 'USD' | null) ?? 'EUR',
        overLimitAllowed: project.overLimitAllowed ?? false,
        // Petra staging UAT 2026-05-18 — hydrate from backend so edit save
        // does not blow away existing config.
        alertThresholds: (project.alertThresholds ?? [])
          .map((t) => t.percentage)
          .join(', '),
        // Phase 53 (Plan 53-04 / COVERAGE-08) — hydrate rollover config.
        rolloverPolicy: (project.rolloverPolicy as 'none' | 'monthly' | 'annual') ?? 'none',
        rolloverCapHours: project.rolloverCapHours != null ? String(project.rolloverCapHours) : '',
        // Edit path leaves contract metadata alone; this Page does not own
        // the SLA Support edit surface — admin SLA Tiers / contract pages do.
        contractStart: '',
        contractEnd: '',
        contractFrequency: 'monthly',
        // Phase 72 Plan 06 (CUR-09, D-07) — hydrate the persisted T&M
        // billing currency; legacy rows default to 'CZK' server-side.
        tmBillingCurrency: (project.tmBillingCurrency as 'CZK' | 'EUR' | 'USD') ?? 'CZK',
      });
      // Initialize selected labels from project. MKS-550 — the two billability labels
      // never enter the picker's state, so a save cannot carry them back (the backend
      // drops them from the body as well; this only keeps the form's own state honest).
      setSelectedLabelIds(
        (project.labels ?? [])
          .filter((l) => !billabilityLabelsReadOnly || !isBillabilityLabelName(l.name))
          .map((l) => l.id),
      );
      // Petra staging UAT 2026-05-18 — hydrate per-role overage rates so
      // saving the edit form keeps them instead of wiping the table.
      setOverageRates(
        (project.overageRates ?? []).map((r) => ({
          roleId: String(r.roleId),
          hourlyRate: r.hourlyRate,
        })),
      );
      // Phase 58 (PAY-FIX-02) — hydrate Fixed Price contract fields on edit.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const proj = project as any;
      setContractAmount(proj.contractAmount != null ? String(proj.contractAmount) : '');
      setContractCurrency(proj.contractCurrency ?? '');
      // Phase 41 Plan 41-10 (Surface 1) — hydrate trio from existing project.
      setTrio({
        paymentTypeId: project.paymentTypeId ?? null,
        projectTypeId: project.projectTypeId ?? null,
        taskTypeId: project.taskTypeId ?? null,
      });
      // 260727-czj — hydrate the in-progress move allowlist from the
      // persisted project value.
      setInProgressAllowedRoleIds(project.inProgressAllowedRoleIds ?? []);
    }
  }, [project, isEditing, billabilityLabelsReadOnly]);

  // 260727-czj — toggle a role in/out of the in-progress move allowlist and
  // persist immediately (each checkbox fires its own mutation, mirroring the
  // other Task States settings that save on interaction).
  function handleToggleInProgressAllowedRole(roleId: number): void {
    if (!projectId) return;
    const next = inProgressAllowedRoleIds.includes(roleId)
      ? inProgressAllowedRoleIds.filter((id) => id !== roleId)
      : [...inProgressAllowedRoleIds, roleId];
    setInProgressAllowedRoleIds(next);
    updateMutation.mutate({ id: projectId, input: { inProgressAllowedRoleIds: next } });
  }

  // Pre-fill project manager from parent project when creating subproject
  useEffect(() => {
    if (!isEditing && parentProject?.projectManager?.id) {
      setForm((prev) => ({
        ...prev,
        projectManagerId: parentProject.projectManager!.id.toString(),
      }));
    }
  }, [isEditing, parentProject]);

  // UAT 2026-05-13 — auto-sync Project type from PaymentType.recommendedProjectTypeId
  // as a sensible default after Payment selection. Petra UAT 2026-05-26 — fire
  // the auto-sync ONLY when Payment changes (tracked via ref), not every time
  // Project type changes. Previously `trio.projectTypeId` was in the deps and
  // the effect snapped the dropdown back to `recommended` the moment the user
  // tried to override it (e.g. Payment=SLA Balíček + Project type=Default was
  // impossible). Now manual override sticks.
  const { data: paymentTypesData } = usePaymentTypes();
  // MKS-251 — the other two registries, read only to name the before / after
  // template in the type-change confirmation.
  const { data: projectTypesData } = useProjectTypes();
  const { data: taskTypesData } = useTaskTypes();
  const lastSyncedPaymentRef = useRef<number | null>(null);
  useEffect(() => {
    if (isEditing) return;
    if (trio.paymentTypeId === null) return;
    if (lastSyncedPaymentRef.current === trio.paymentTypeId) return;
    lastSyncedPaymentRef.current = trio.paymentTypeId;
    const pt = paymentTypesData?.data?.find((t) => t.id === trio.paymentTypeId);
    const recommended = pt?.recommendedProjectTypeId;
    if (recommended == null) return;
    setTrio((prev) =>
      prev.projectTypeId === recommended ? prev : { ...prev, projectTypeId: recommended },
    );
  }, [trio.paymentTypeId, paymentTypesData, isEditing]);

  // UAT 2026-05-13 — auto-seed SLA Task tiers when the admin first picks
  // SLA Task. Create path only; edit path leaves existing tiers alone.
  useEffect(() => {
    if (isEditing) return;
    if (trio.taskTypeId !== SLA_TASK_TYPE_ID) return;
    if (slaTiers.length > 0) return;
    setSlaTiers(DEFAULT_SLA_TIERS.map((t) => ({ ...t })));
  }, [trio.taskTypeId, slaTiers.length, isEditing]);

  // Phase 58 (PAY-TM-02) — auto-seed one empty rate row when T&M is selected
  // and no rates exist yet. UI-SPEC interaction state: "At least one rate row
  // auto-seeded". Fires on both create and edit paths.
  // quick 261103-rwl — seeded for billable AND non-billable projects so the
  // role picker stays discoverable; for a non-billable project the row is
  // optional and is simply dropped on submit if no role is chosen.
  useEffect(() => {
    if (trio.paymentTypeId !== TIME_AND_MATERIAL_PAYMENT_TYPE_ID) return;
    if (overageRates.length > 0) return;
    setOverageRates([{ roleId: '', hourlyRate: '' }]);
  }, [trio.paymentTypeId, overageRates.length]);

  // UAT 2026-05-13 — auto-seed SLA Support contract + a single Europe/Prague
  // coverage entry when ProjectType first lands on SLA Support (whether
  // manually or via auto-sync from Payment=SLA Balíček). Admin can override
  // every field in the contract config block.
  // Tracks the contract dates this effect last auto-applied, so it can keep them
  // synced to the project duration regardless of the order the user fills fields
  // — WITHOUT clobbering a value the user typed. Once the contract dates diverge
  // from the last auto-applied pair, the effect stops touching them.
  const autoSeededContractRef = useRef<{ start: string; end: string } | null>(null);

  // UAT 2026-05-13 / Petra UAT 2026-06-03 — auto-seed the SLA Support contract +
  // a single Europe/Prague coverage entry when ProjectType lands on SLA Support
  // (manually or via auto-sync from Payment=SLA Balíček). The contract term
  // mirrors the project's own Start/End and re-syncs if the project dates change
  // later (any fill order) — unless the user has manually edited the contract
  // dates, in which case their value is preserved. Falls back to today / today+1y
  // only while the project duration is empty.
  useEffect(() => {
    if (isEditing) return;
    if (trio.projectTypeId !== SLA_SUPPORT_PROJECT_TYPE_ID) return;

    const desiredStart = form.startDate || isoToday();
    const desiredEnd = form.endDate || isoTodayPlusYears(1);
    const ref = autoSeededContractRef.current;
    const untouchedByUser =
      (!form.contractStart && !form.contractEnd) ||
      (ref !== null && form.contractStart === ref.start && form.contractEnd === ref.end);
    if (
      untouchedByUser &&
      (desiredStart !== form.contractStart || desiredEnd !== form.contractEnd)
    ) {
      autoSeededContractRef.current = { start: desiredStart, end: desiredEnd };
      setForm((prev) => ({
        ...prev,
        contractStart: desiredStart,
        contractEnd: desiredEnd,
      }));
    }

    if (coverageEntries.length === 0) {
      setCoverageEntries([
        { tz: 'Europe/Prague', preset: 'business_hours', holidayCountry: 'CZ' },
      ]);
    }
  }, [
    trio.projectTypeId,
    form.startDate,
    form.endDate,
    form.contractStart,
    form.contractEnd,
    coverageEntries.length,
    isEditing,
  ]);

  // Phase 19 D-06 + D-02: default Template dropdown on the create path.
  //   - With ?parentId AND parent.appliedTemplateId is in the active templates list → use parent's
  //   - Otherwise → Minimal built-in
  //   - Defensive: if Minimal isn't in the response, fall back to the first active template
  //
  // Note: parent.appliedTemplateId is now actually serialized by the backend
  // — Plan 19-06 Task 1 added it to projectDetailResponse's allow-list. Prior
  // to that fix the field was silently stripped by fast-json-stringify, so the
  // subproject-default branch never engaged. (See MEMORY.md "Fastify Response
  // Schema Pitfall".)
  useEffect(() => {
    if (isEditing) return;
    if (!templates || templates.length === 0) return;
    // Don't override an explicit user pick.
    setForm((prev) => {
      if (prev.templateId) return prev;
      const parentTemplateId = parentProject?.appliedTemplateId ?? null;
      const parentMatch =
        parentTemplateId != null
          ? templates.find((t) => t.id === parentTemplateId)
          : undefined;
      const minimal = templates.find((t) => t.isBuiltIn && t.name === 'Minimal');
      const fallback = templates[0]; // defensive — non-empty by guard above
      const chosen = parentMatch ?? minimal ?? fallback;
      return chosen ? { ...prev, templateId: String(chosen.id) } : prev;
    });
  }, [isEditing, templates, parentProject?.appliedTemplateId]);

  // Pre-fill from lead data when creating project from a won lead
  useEffect(() => {
    if (!isEditing && leadData?.data) {
      const lead = leadData.data;
      if (leadRole === 'nonBillable') {
        // Deal value belongs to the billable project; mirror the modal's
        // non-billable naming default and preset the flag.
        setForm((prev) => ({
          ...prev,
          name: `${lead.customer.name ?? ''} — non-billable`.trim(),
          description: '',
          customerId: String(lead.customer.id),
          billable: false,
        }));
      } else {
        setForm((prev) => ({
          ...prev,
          name: lead.name,
          moneyTotal: lead.dealValue ? parseFloat(lead.dealValue).toString() : '',
          // MKS-263 — the deal value already seeded the money budget, which a
          // T&M project now reads as "Estimated maximum". Carry its currency
          // too: without this a EUR deal landed as a CZK budget, so the
          // consumption alerts measured against the wrong number.
          ...(isPersistCurrency(lead.dealValueCurrency)
            ? { moneyTotalCurrency: lead.dealValueCurrency }
            : {}),
          description: '',
          customerId: String(lead.customer.id),
        }));
      }
    }
  }, [isEditing, leadData, leadRole]);

  // MKS-261 — carry the lead's billing type into the create form's Payment
  // selector. `Lead.billingTypeId` is a PaymentType FK (MKS-94), so a lead
  // agreed as SLA balíček lands on SLA balíček and its package fields open
  // straight away, rather than on the T&M seed somebody then has to correct.
  //
  // Only the three create-time types are honoured: a lead left on Default (or
  // on a type that is not offered at create) prefills nothing, so the form can
  // never seed a value the backend create gate would turn around and reject.
  //
  // One shot, and never over a pick the user has already made — the lead query
  // resolves after the T&M seed above has run, so without the ref this would
  // re-apply on every `leadData` identity change.
  const seededLeadPaymentTypeRef = useRef(false);
  useEffect(() => {
    if (isEditing) return;
    if (leadRole === 'nonBillable') return;
    if (seededLeadPaymentTypeRef.current || paymentTypePickedRef.current) return;
    const billingTypeId = leadData?.data?.billingTypeId;
    if (billingTypeId == null || !createPaymentTypeIds.includes(billingTypeId)) return;
    seededLeadPaymentTypeRef.current = true;
    setTrio((prev) => ({ ...prev, paymentTypeId: billingTypeId }));
    // createPaymentTypeIds is a literal rebuilt each render; its contents are
    // the three module-level constants, so it never actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, leadRole, leadData]);

  // Click-outside handler for customer dropdown
  useEffect(() => {
    if (!isCustomerDropdownOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target as Node)) {
        setIsCustomerDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCustomerDropdownOpen]);

  async function handleAddState(name: string, color: string) {
    try {
      // User UAT 2026-06-10 — backend Zod requires `ordering` on POST
      // (createStateSchema in backend/src/api/schemas/taskState.ts:28). The
      // Phase 20 docstring originally claimed the server auto-picked
      // ordering; that was incorrect. Caller now sends append-to-end.
      const orderingBase = projectStates?.length ?? 0;
      await createStateMutation.mutateAsync({
        name,
        color,
        ordering: orderingBase,
      });
    } catch (err) {
      setTaskStatesToast({
        message: err instanceof Error ? err.message : 'Failed to add state',
        variant: 'error',
      });
    }
  }

  async function handleRenameState(stateId: number, newName: string) {
    try {
      await updateStateMutation.mutateAsync({ stateId, patch: { name: newName } });
    } catch (err) {
      setTaskStatesToast({
        message: err instanceof Error ? err.message : 'Failed to rename state',
        variant: 'error',
      });
    }
  }

  async function handleRecolorState(stateId: number, newColor: string) {
    try {
      await updateStateMutation.mutateAsync({ stateId, patch: { color: newColor } });
    } catch (err) {
      setTaskStatesToast({
        message: err instanceof Error ? err.message : 'Failed to recolor state',
        variant: 'error',
      });
    }
  }

  async function handleDeleteState(stateId: number) {
    setDeleteConflict(null);
    try {
      await deleteStateMutation.mutateAsync(stateId);
    } catch (err) {
      if (isStateInUseError(err)) {
        // STATE-04 / D-20: SINGLE surface — the inline red banner with the
        // [View tasks] deep-link is the authoritative UI. Do NOT also fire
        // AppToast for this case (the previous draft did, producing a
        // duplicate UX message; D-20 specifies one toast with a deep-link
        // action and the inline banner already covers that contract).
        const c = err.details.conflicts[0]; // single-state delete → length 1 (Phase 18 D-03)
        if (c) {
          setDeleteConflict({
            stateId: c.stateId,
            stateName: c.stateName,
            taskCount: c.taskCount,
          });
        }
      } else {
        // All other errors keep using AppToast as before.
        setTaskStatesToast({
          message: err instanceof Error ? err.message : 'Failed to delete state',
          variant: 'error',
        });
      }
    }
  }

  // 260723-tst — flip a state's terminal flag (display/edit only; suppresses
  // the age 💩 emoji for tasks in this state).
  // 260726-wip — terminal and "work in progress" are mutually exclusive;
  // turning terminal ON clears WIP (backend enforces too — paired patch keeps
  // the optimistic UI consistent).
  async function handleToggleTerminalState(stateId: number, next: boolean) {
    try {
      await updateStateMutation.mutateAsync({
        stateId,
        patch: next ? { isTerminal: true, isInProgress: false } : { isTerminal: false },
      });
    } catch (err) {
      setTaskStatesToast({
        message: err instanceof Error ? err.message : 'Failed to update state',
        variant: 'error',
      });
    }
  }

  // 260726-wip — flip a state's "work in progress" flag. Turning it ON clears
  // the mutually-exclusive terminal flag.
  async function handleToggleInProgressState(stateId: number, next: boolean) {
    try {
      await updateStateMutation.mutateAsync({
        stateId,
        patch: next ? { isInProgress: true, isTerminal: false } : { isInProgress: false },
      });
    } catch (err) {
      setTaskStatesToast({
        message: err instanceof Error ? err.message : 'Failed to update state',
        variant: 'error',
      });
    }
  }

  async function handleReorderStates(orderedIds: number[]) {
    // Race-guard: optimistic create injects a synthetic NEGATIVE id into
    // the cache (`useCreateProjectState` in plan 20-03). If the user drags
    // before the create settles, the negative id would leak into the
    // reorder body and the backend rejects via z.number().int().positive().
    // Skip dispatch — the next reorder after settle will dispatch normally.
    if (orderedIds.some((id) => id <= 0)) {
      return;
    }
    try {
      await reorderStatesMutation.mutateAsync(orderedIds);
    } catch (err) {
      setTaskStatesToast({
        message: err instanceof Error ? err.message : 'Failed to reorder states',
        variant: 'error',
      });
    }
  }

  // The probe answers fast and a 403 ends the wait (it never hangs); only a viewer who has no
  // General view to fall back on waits for it.
  const awaitingEngineeringAccess =
    isEditing && !generalCapableViewer && engineeringAccess.isLoading;

  if ((isEditing && projectLoading) || usersLoading || awaitingEngineeringAccess) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    );
  }

  // Phase 129 (D-05) — member-only view: a team member with no General view sees the project's
  // name and the Engineering content, no tab bar.
  if (engineeringOnly && project && projectId !== undefined) {
    return (
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-gray-900">
            {project.fullName || project.name}
          </h1>
          <Button variant="ghost" onClick={handleCancel}>
            Close
          </Button>
        </div>
        <ProjectEngineeringTab projectId={projectId} />
      </div>
    );
  }

  // Phase 129 (D-05) — a finance user who is also a team member: General is the billing cards
  // only (as today), plus the Engineering tab.
  if (billingTermsOnly && showEngineering && project && projectId !== undefined) {
    return (
      <div
        className={`${tab === 'engineering' ? 'max-w-7xl' : 'max-w-2xl'} mx-auto space-y-6`}
      >
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-gray-900">
            {project.fullName || project.name}
          </h1>
          <Button variant="ghost" onClick={handleCancel}>
            Close
          </Button>
        </div>
        {projectTabBar}
        <div hidden={tab === 'engineering'} className="space-y-6">
          <ProjectBillingTermsSection project={project} canEdit />
          <BillingScheduleSection projectId={project.id} canEdit />
        </div>
        {engineeringMounted && (
          <div hidden={tab !== 'engineering'}>
            <ProjectEngineeringTab projectId={projectId} />
          </div>
        )}
      </div>
    );
  }

  // MKS-94 — finance-only view: the billing terms are the one thing this user
  // is allowed to change on the project, so they are the whole page.
  if (billingTermsOnly && project) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-gray-900">
            {project.fullName || project.name}
          </h1>
          <Button variant="ghost" onClick={handleCancel}>
            Close
          </Button>
        </div>
        <ProjectBillingTermsSection project={project} canEdit />
        <BillingScheduleSection projectId={project.id} canEdit />
      </div>
    );
  }

  // UAT 2026-05-13 — every error path scrolls the offending field / surface
  // into view + focuses it so the user immediately sees what needs fixing
  // (instead of the message appearing far above the submit button while the
  // page stays scrolled to the bottom). MKS-251 hoisted this out of the submit
  // catch block so the client-side type gates can use it too.
  const scrollFocus = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    // jsdom (and any environment without a layout engine) has no
    // scrollIntoView; the field error itself is the point, the scroll is a
    // nicety, so never let its absence throw out of the submit handler.
    if (typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    if (typeof (el as HTMLInputElement).focus === 'function') {
      // Defer focus until after smooth scroll starts so iOS / older Safari
      // doesn't jump back to the field with an instant scroll.
      // F2-012: scheduled via useTrackedTimeout so unmount clears it.
      scheduleFocus(() => (el as HTMLInputElement).focus(), 200);
    }
  };

  // quick 261103-rwl — per-role T&M rates are only mandatory for billable
  // projects. Plain constant (not a hook) so hook order is unaffected.
  const tmRatesRequired = isTmRateRequired(form.billable);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // UAT 2026-05-13 defense-in-depth: even though the submit Button is
    // `disabled={isLoading}`, an in-flight mutation must hard-block re-entry
    // here so a stray Enter / double-click can't fire a parallel request
    // that would race the slug-allocation pre-check and create a ghost
    // project on the second submit (Phase 27 D-12 TOCTOU is the user-side
    // symptom).
    if (createMutation.isPending || updateMutation.isPending) {
      return;
    }
    setError(null);
    setSlugError(null); // Phase 27 D-13: clear field error on each submit

    if (!form.name.trim()) {
      setError('Project name is required.');
      return;
    }

    if (!isEditing && !form.templateId) {
      setError('Project template is required.');
      return;
    }

    // Phase 68 (Plan 68-06 / D-10 / R8) — client-side start/end gate for all
    // payment types. Mirrors the backend service gate so the error is surfaced
    // locally before the network round-trip.
    if (!isEditing && (!form.startDate || !form.endDate)) {
      setError('Start date and end date are required for all projects.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Phase 53 WR-05: guard rolloverCapHours parse — parseInt on a non-numeric
    // string returns NaN which JSON serialises to null, silently dropping the cap.
    if (form.rolloverCapHours.trim() !== '') {
      const capNum = parseInt(form.rolloverCapHours.trim(), 10);
      if (isNaN(capNum) || capNum < 0) {
        setError('Rollover cap must be a non-negative integer.');
        return;
      }
    }

    // 260913-jpy — guard the staleness thresholds client-side, mirroring the
    // backend service gate, so a bad value never trips an opaque AJV 400.
    if (form.stalenessIndicatorEnabled) {
      const staleNum = parseInt(form.stalenessStaleDays.trim(), 10);
      const graveyardNum = parseInt(form.stalenessGraveyardDays.trim(), 10);
      const inRange = (n: number) => Number.isInteger(n) && n >= 1 && n <= 365;
      if (!inRange(staleNum) || !inRange(graveyardNum)) {
        setError('Staleness thresholds must be whole numbers between 1 and 365.');
        return;
      }
      if (!(staleNum < graveyardNum)) {
        setError('The stale threshold must be less than the graveyard threshold.');
        return;
      }
    }

    // MKS-251 — the type-specific fields are required by the backend; check
    // them here so a missing contract amount or per-role rate surfaces as a
    // field-level error next to the input instead of a 400 from invoicing
    // weeks later.
    if (!validateTypeSpecificFields()) return;

    // MKS-251 — a type change on an existing project is explained and
    // confirmed before it is saved. No typed project name: the confirmation
    // states what the change does and does not touch, and the user says yes.
    if (isEditing && project) {
      const consequences = describeTypeChange();
      if (consequences) {
        setPendingTypeChange(consequences);
        return;
      }
    }

    await submitForm();
  };

  /**
   * MKS-251 — client-side mirror of the backend's per-template required-field
   * gates (SLA balíček hours/amount/currency + overage rates, Fixed Price
   * contract amount + currency, T&M per-role rates). Returns false and leaves
   * an inline error on the offending field when something is missing.
   */
  const validateTypeSpecificFields = (): boolean => {
    setBalicekError(null);
    if (trio.paymentTypeId === SLA_BALICEK_PAYMENT_TYPE_ID) {
      if (!form.balicekHours.trim() || !form.balicekAmount.trim() || !form.balicekCurrency) {
        setBalicekError(
          'SLA Balíček needs monthly hours, an amount, and a billing currency.',
        );
        scrollFocus('project-form-balicek-hours');
        return false;
      }
      if (
        form.overLimitAllowed &&
        overageRates.filter((r) => r.roleId && r.hourlyRate.trim()).length === 0
      ) {
        setBalicekError(
          'Over-limit hours are enabled — add at least one overage rate (role + hourly rate) below.',
        );
        scrollFocus('project-form-balicek-overlimit');
        return false;
      }
    }
    if (trio.paymentTypeId === FIXED_PRICE_PAYMENT_TYPE_ID) {
      if (!contractAmount.trim()) {
        setBalicekError('Contract amount is required for Fixed Price projects.');
        scrollFocus('project-form-contract-amount');
        return false;
      }
      if (!contractCurrency) {
        setBalicekError('Currency is required for Fixed Price projects.');
        scrollFocus('project-form-contract-currency');
        return false;
      }
    }
    // quick 261103-rwl — per-role rates are only mandatory for billable projects.
    if (trio.paymentTypeId === TIME_AND_MATERIAL_PAYMENT_TYPE_ID && tmRatesRequired) {
      if (overageRates.filter((r) => r.roleId && r.hourlyRate.trim()).length === 0) {
        setBalicekError(
          'At least one per-role rate is required for Time & Material projects.',
        );
        scrollFocus('project-form-tm-rates');
        return false;
      }
    }
    return true;
  };

  /**
   * MKS-251 — plain-language description of a pending type change, or null when
   * the trio is unchanged. Each line names one of the three types; the closing
   * line is the part people actually worry about: already-issued invoices and
   * approved hours are history and stay exactly as they are.
   */
  const describeTypeChange = (): string[] | null => {
    if (!project) return null;
    const label = (
      list: Array<{ id: number; name: string }> | undefined,
      id: number | null,
    ) => (id == null ? 'Default' : (list?.find((t) => t.id === id)?.name ?? `#${id}`));
    const lines: string[] = [];
    if (trio.paymentTypeId !== (project.paymentTypeId ?? null)) {
      lines.push(
        `Payment: ${label(paymentTypesData?.data, project.paymentTypeId ?? null)} → ${label(
          paymentTypesData?.data,
          trio.paymentTypeId,
        )}`,
      );
    }
    // MKS-264 — the SLA Support checkbox writes the baseline id where the
    // dropdown used to leave null, so compare the two as the same value:
    // unticking the box on a project whose stored type is null is not a
    // change, and must not raise a "Default → Default" confirmation.
    const projectTypeOf = (id: number | null) => id ?? BASELINE_TEMPLATE_ID;
    if (projectTypeOf(trio.projectTypeId) !== projectTypeOf(project.projectTypeId ?? null)) {
      lines.push(
        `Project type: ${label(projectTypesData?.data, project.projectTypeId ?? null)} → ${label(
          projectTypesData?.data,
          trio.projectTypeId,
        )}`,
      );
    }
    if (trio.taskTypeId !== (project.taskTypeId ?? null)) {
      lines.push(
        `Task template: ${label(taskTypesData?.data, project.taskTypeId ?? null)} → ${label(
          taskTypesData?.data,
          trio.taskTypeId,
        )}`,
      );
    }
    return lines.length > 0 ? lines : null;
  };

  const submitForm = async () => {
    try {
      // Phase 27 D-13: blank slug → undefined → server auto-derives from name.
      const trimmedSlug = form.slug.trim().toUpperCase();

      // Base payload (no slug — handled per-branch to enforce D-07 immutability)
      const basePayload = {
        name: form.name.trim(),
        fullName: form.fullName.trim() || undefined,
        description: form.description.trim() || undefined,
        parentId: form.parentId ? parseInt(form.parentId, 10) : undefined,
        customerId: form.customerId ? parseInt(form.customerId, 10) : undefined,
        projectManagerId: form.projectManagerId ? parseInt(form.projectManagerId, 10) : undefined,
        startDate: form.startDate || undefined,
        endDate: form.endDate || undefined,
        defaultRate: form.defaultRate ? parseFloat(form.defaultRate) : undefined,
        hoursTotal: form.hoursTotal ? parseFloat(form.hoursTotal) : undefined,
        moneyTotal: form.moneyTotal ? parseFloat(form.moneyTotal) : undefined,
        // Phase 72 (currency-aware budgets) — persisted unconverted alongside
        // moneyTotal; included on both create and update payloads.
        moneyTotalCurrency: form.moneyTotalCurrency,
        // Phase 72 Plan 06 (CUR-09, D-07) — single project-level T&M billing
        // currency; included on both create and update payloads (mirrors
        // moneyTotalCurrency above — never-null, no "unset" state).
        tmBillingCurrency: form.tmBillingCurrency,
        isDefault: form.isDefault,
        mandatoryTasks: form.mandatoryTasks,
        allowExternalIntake: form.allowExternalIntake,
        // 260727-gob — open-board flag; included on both create and update
        // so the settings toggle actually persists.
        openToAllInternal: form.openToAllInternal,
        // Phase 62 (Plan 62-06, TBI-01) — billable flag; included on both
        // create and update so the To Be Invoiced queue reflects the toggle.
        billable: form.billable,
        // F3M-01 — disable hour reporting flag; included on both create and
        // update so admin can toggle it from the project form.
        timeReportingDisabled: form.timeReportingDisabled,
        // 260913-jpy — staleness indicator config; included on both create
        // and update. The two day counts are parsed as base-ten integers,
        // falling back to the imported global constants if a parse somehow
        // yields a non-finite value — never send a non-numeric value, since
        // that would trip AJV and surface as an opaque 400 (the client-side
        // guard above should already have caught this, but this is the
        // last-resort safety net).
        stalenessIndicatorEnabled: form.stalenessIndicatorEnabled,
        stalenessStaleDays: Number.isFinite(parseInt(form.stalenessStaleDays.trim(), 10))
          ? parseInt(form.stalenessStaleDays.trim(), 10)
          : TASK_STALE_THRESHOLD_DAYS,
        stalenessGraveyardDays: Number.isFinite(parseInt(form.stalenessGraveyardDays.trim(), 10))
          ? parseInt(form.stalenessGraveyardDays.trim(), 10)
          : TASK_GRAVEYARD_THRESHOLD_DAYS,
      };

      if (isEditing && projectId) {
        // UAT 2026-05-13 F-D6/Issue-B — when editing an SLA Balíček project,
        // forward the balíček config fields via PATCH /:id (F-D6 added support).
        // SLA Support contract + SLA Task tier editing on this page deferred —
        // both have dedicated admin pages (SLA Tiers, coverage editor).
        const isSlaBalicekEdit = trio.paymentTypeId === SLA_BALICEK_PAYMENT_TYPE_ID;
        const parseThresholdsForEdit = (csv: string) =>
          csv
            .split(',')
            .map((s) => parseInt(s.trim(), 10))
            .filter((n) => Number.isInteger(n) && n >= 1 && n <= 200)
            .map((percentage) => ({ percentage }));
        // D-07: slug is immutable — never included in update payload.
        const updatePayload = {
          ...basePayload,
          parentId: form.parentId ? parseInt(form.parentId, 10) : null,
          labelIds: selectedLabelIds,
          // MKS-251 — the template trio travels with the ordinary update now.
          // Only sent when the user may change it: the backend's trio gate
          // (admin / finance / project PM, plus the release flag) answers 403
          // or 404 for the fields' mere presence, which would block a manager
          // from saving a rename.
          ...(canEditTypes && {
            paymentTypeId: trio.paymentTypeId,
            projectTypeId: trio.projectTypeId,
            taskTypeId: trio.taskTypeId,
          }),
          ...(isSlaBalicekEdit && form.balicekHours && {
            balicekHours: parseInt(form.balicekHours, 10),
            balicekAmount: form.balicekAmount,
            balicekCurrency: form.balicekCurrency as 'CZK' | 'EUR' | 'USD',
            overLimitAllowed: form.overLimitAllowed,
            alertThresholds: parseThresholdsForEdit(form.alertThresholds),
            // Petra staging UAT 2026-05-18 — update path now ships
            // overageRates alongside alertThresholds (atomic replace on
            // the backend). Empty hourlyRate / missing roleId rows are
            // dropped so partial UI state cannot trip the schema.
            ...(form.overLimitAllowed && {
              overageRates: overageRates
                .filter((r) => r.roleId && r.hourlyRate.trim())
                .map((r) => ({
                  roleId: parseInt(r.roleId, 10),
                  hourlyRate: r.hourlyRate.trim(),
                })),
            }),
          }),
          // Phase 53 (Plan 53-04 / COVERAGE-08) — always send rollover fields
          // when balíček is active (even when policy='none') so the backend
          // can reset a previously-configured rollover. Separate spread so
          // it is not gated by form.balicekHours (which may be empty on forms
          // opened before hours were set).
          ...(isSlaBalicekEdit && {
            rolloverPolicy: form.rolloverPolicy,
            rolloverCapHours:
              form.rolloverCapHours.trim() !== ''
                ? parseInt(form.rolloverCapHours, 10)
                : null,
          }),
          // MKS-205 — budget-consumption alerts on Fixed Price / T&M. Sent
          // unconditionally (like the balíček edit above) so clearing the
          // field removes the thresholds rather than silently keeping them.
          ...((trio.paymentTypeId === FIXED_PRICE_PAYMENT_TYPE_ID ||
            trio.paymentTypeId === TIME_AND_MATERIAL_PAYMENT_TYPE_ID) && {
            alertThresholds: parseThresholdsForEdit(form.alertThresholds),
          }),
          // MKS-251 — Fixed Price contract fields on the edit path. Previously
          // create-only, which is why switching a project onto Fixed Price had
          // to go through the wizard: the backend gate requires both.
          ...(trio.paymentTypeId === FIXED_PRICE_PAYMENT_TYPE_ID && {
            contractAmount,
            contractCurrency: contractCurrency as 'CZK' | 'EUR' | 'USD',
          }),
          // MKS-251 — T&M per-role rates on the edit path (synced by diff on the
          // backend). quick 261103-rwl — non-billable: every row with a role is
          // sent, a blank rate as '0'; billable: role + rate required.
          ...tmOverageRatesPatch(
            trio.paymentTypeId === TIME_AND_MATERIAL_PAYMENT_TYPE_ID,
            overageRates,
            form.billable,
          ),
        };
        await updateMutation.mutateAsync({ id: projectId, input: updatePayload });
        navigate(`/projects/${projectId}`);
      } else {
        // Phase 37 (Plan 37-08) — SLA balíček satellite fields. Service-layer
        // gate requires all three when paymentTypeId === SLA_BALICEK_PAYMENT_TYPE_ID.
        const isSlaBalicek = trio.paymentTypeId === SLA_BALICEK_PAYMENT_TYPE_ID;
        // Phase 36 (Plan 36-05) — SLA Support contract + coverage.
        const isSlaSupport = trio.projectTypeId === SLA_SUPPORT_PROJECT_TYPE_ID;
        // Phase 38 (Plan 38-05) — SLA Task tiers. Convert UI shape (hours, csv)
        // to backend shape (minutes, number[]). Filter out invalid threshold
        // entries (non-integer / out-of-range) to keep submit-side resilience.
        const isSlaTask = trio.taskTypeId === SLA_TASK_TYPE_ID;
        const parseThresholdCsv = (csv: string): number[] =>
          csv
            .split(',')
            .map((s) => parseInt(s.trim(), 10))
            .filter((n) => Number.isInteger(n) && n >= 1 && n <= 200);
        const submittableSlaTiers = isSlaTask
          ? slaTiers.map((t) => ({
              name: t.name.trim(),
              orderIndex: t.orderIndex,
              responseTargetMinutes: Math.max(1, Math.round(parseFloat(t.responseHours) * 60)),
              resolutionTargetMinutes: Math.max(1, Math.round(parseFloat(t.resolutionHours) * 60)),
              responseWarningThresholds: parseThresholdCsv(t.responseWarningCsv),
              resolutionWarningThresholds: parseThresholdCsv(t.resolutionWarningCsv),
            }))
          : [];
        // Parse "80, 100, 120" → [{percentage: 80}, {percentage: 100}, {percentage: 120}].
        // Backend constraint: integer 1..200. Filters out non-numeric / out-of-range entries.
        const parsedThresholds = form.alertThresholds
          .split(',')
          .map((s) => parseInt(s.trim(), 10))
          .filter((n) => Number.isInteger(n) && n >= 1 && n <= 200)
          .map((percentage) => ({ percentage }));
        const result = await createMutation.mutateAsync({
          ...basePayload,
          // Phase 27 D-13: blank slug → undefined → server auto-derives from name
          slug: trimmedSlug || undefined,
          labelIds: selectedLabelIds,
          projectTemplateId: parseInt(form.templateId, 10),
          // Phase 41 Plan 41-10 (Surface 1) — template trio.
          // null is preserved as a deliberate "no template chosen" signal.
          paymentTypeId: trio.paymentTypeId,
          projectTypeId: trio.projectTypeId,
          taskTypeId: trio.taskTypeId,
          ...(isSlaSupport && {
            contractStart: form.contractStart,
            contractEnd: form.contractEnd,
            contractFrequency: form.contractFrequency as
              | 'monthly'
              | 'quarterly'
              | 'yearly'
              | 'one_off'
              | 'perpetual',
            coverageEntries: coverageEntries.map((c, i) => ({
              tz: c.tz,
              preset: c.preset,
              holidayCountry: c.holidayCountry,
              orderIndex: i,
            })),
          }),
          ...(isSlaBalicek && {
            balicekHours: parseInt(form.balicekHours, 10),
            balicekAmount: form.balicekAmount,
            balicekCurrency: form.balicekCurrency as 'CZK' | 'EUR' | 'USD',
            overLimitAllowed: form.overLimitAllowed,
            ...(parsedThresholds.length > 0 && {
              alertThresholds: parsedThresholds,
            }),
            // PAY-03 — when overLimit=true the backend requires ≥1 overage
            // rate. Filter out empty rows (no role / no rate) so partial UI
            // state doesn't trip schema validation downstream.
            ...(form.overLimitAllowed && {
              overageRates: overageRates
                .filter((r) => r.roleId && r.hourlyRate.trim())
                .map((r) => ({
                  roleId: parseInt(r.roleId, 10),
                  hourlyRate: r.hourlyRate.trim(),
                })),
            }),
            // Phase 53 (Plan 53-04 / COVERAGE-08) — rollover config on create.
            // Send only when non-default to keep the payload minimal; backend
            // DEFAULT 'none' applies when absent.
            ...(form.rolloverPolicy !== 'none' && {
              rolloverPolicy: form.rolloverPolicy,
              rolloverCapHours:
                form.rolloverCapHours.trim() !== ''
                  ? parseInt(form.rolloverCapHours, 10)
                  : null,
            }),
          }),
          ...(isSlaTask && { slaTiers: submittableSlaTiers }),
          // Phase 58 (PAY-FIX-02, D-03) — Fixed Price contract fields.
          ...(trio.paymentTypeId === FIXED_PRICE_PAYMENT_TYPE_ID && {
            contractAmount,
            contractCurrency,
            // MKS-205 — budget-consumption alerts. Sent only when the admin
            // typed some; Fixed Price gets no default seed.
            ...(parsedThresholds.length > 0 && { alertThresholds: parsedThresholds }),
          }),
          // Phase 58 (PAY-TM-02, D-03) — Time & Material per-role rates.
          // Send overageRates unconditionally for T&M (not gated by overLimitAllowed
          // which is a SLA Balíček-only concept).
          // quick 261103-rwl — non-billable: every row with a role is sent,
          // a blank rate as '0'; billable: unchanged (role + rate required).
          ...tmOverageRatesPatch(
            trio.paymentTypeId === TIME_AND_MATERIAL_PAYMENT_TYPE_ID,
            overageRates,
            form.billable,
          ),
          // MKS-205 — budget-consumption alerts (see the Fixed Price spread).
          ...(trio.paymentTypeId === TIME_AND_MATERIAL_PAYMENT_TYPE_ID &&
            parsedThresholds.length > 0 && { alertThresholds: parsedThresholds }),
        });
        // Link lead to newly created project
        if (fromLeadId) {
          await updateLeadMutation.mutateAsync({
            id: fromLeadId,
            input:
              leadRole === 'nonBillable'
                ? { nonBillableProjectId: result.id }
                : { convertedProjectId: result.id },
          });
          // Came from the lead's Set up projects modal — return there (the
          // ?setupProjects deep-link reopens the modal) instead of the
          // project detail, so the user can finish the other half.
          navigate(`/sales/leads?setupProjects=${fromLeadId}`);
          return;
        }
        navigate(`/projects/${result.id}`);
      }
    } catch (err) {
      // Phase 27 D-13: 409 = slug collision → field-level error.
      // UAT 2026-05-13: extract the offending slug from the backend message
      // ("Project slug 'XYZ' is already in use…") so the user understands
      // — especially when the slug field is BLANK and the server auto-
      // derived a 3-char from the project name.
      const apiErr = err as {
        statusCode?: number;
        message?: string;
        code?: string;
        details?: { code?: string; issues?: TmReadinessIssue[] };
      };
      // MKS-372 — the backend refused the conversion. The dialog normally
      // catches this first, but the project can go stale between the check and
      // the save (somebody edits Team Assignments in another tab), so the
      // items come back here too and are listed at the top of the form.
      const tmDetails = apiErr?.details;
      if (
        apiErr?.code === 'tm_conversion_not_ready' ||
        tmDetails?.code === 'tm_conversion_not_ready'
      ) {
        setTmConversionIssues(tmDetails?.issues ?? []);
        setTmConversionReady(false);
        setError(
          apiErr.message ??
            'This project is not ready for Time & Material yet. Fix the items below first.',
        );
        scrollFocus('project-form-tm-rates');
        return;
      }
      if (apiErr?.statusCode === 409) {
        const conflictMsg = apiErr.message ?? '';
        const match = conflictMsg.match(/slug '([^']+)'/);
        const takenSlug = match ? match[1] : null;
        const wasBlank = form.slug.trim() === '';
        if (takenSlug && wasBlank) {
          setSlugError(
            `The slug '${takenSlug}' (auto-derived from the project name) is already used by another project. Type a unique 3-character slug here (e.g. 'X99'), or rename the project so a different slug is derived.`,
          );
        } else if (takenSlug) {
          setSlugError(
            `The slug '${takenSlug}' is already used by another project. Choose a different 3-character code.`,
          );
        } else {
          setSlugError(conflictMsg || 'That slug is already in use. Choose a different 3-character code.');
        }
        scrollFocus('project-form-slug');
        return;
      }
      // UAT 2026-05-13 — surface SLA balíček validation gates inline near
      // the offending field. PAY-03 = overage rate required; PAY-10 = alert
      // threshold required when overLimit is ON.
      const msg = apiErr?.message ?? '';
      // Phase 58 — T&M validation gate (PAY-TM-02): ≥1 per-role rate required
      // (billable projects only). quick 261103-rwl — must run BEFORE the SLA
      // branch below: its generic /overage rate/ regex also matches the T&M
      // backend message and would show the wrong over-limit text.
      if (isTmMissingRatesError(msg)) {
        setBalicekError('At least one per-role rate is required for Time & Material projects.');
        scrollFocus('project-form-tm-rates');
        return;
      }
      if (msg.includes('PAY-03') || /overage rate/i.test(msg)) {
        setBalicekError(
          'Over-limit hours are enabled — add at least one overage rate (role + hourly rate) below.',
        );
        scrollFocus('project-form-balicek-overlimit');
        return;
      }
      if (msg.includes('PAY-10') || /alert threshold/i.test(msg)) {
        setBalicekError(
          'Over-limit hours are enabled — add at least one alert threshold percentage (e.g. 80, 100).',
        );
        scrollFocus('project-form-balicek-thresholds');
        return;
      }
      // Phase 68 (Plan 68-06 / D-10 / R8) — required start/end gate.
      if (/Project requires startDate and endDate/i.test(msg)) {
        setError('Start date and end date are required for all projects.');
        scrollFocus('project-form-start-date');
        return;
      }
      // Phase 58 — Fixed Price validation gates (PAY-FIX-02).
      if (/contractAmount is required|contract amount.*required/i.test(msg)) {
        setBalicekError('Contract amount is required for Fixed Price projects.');
        scrollFocus('project-form-contract-amount');
        return;
      }
      if (/contractCurrency is required|currency.*required.*Fixed Price/i.test(msg)) {
        setBalicekError('Currency is required for Fixed Price projects.');
        scrollFocus('project-form-contract-currency');
        return;
      }
      // Friendly fallback for SLA Support gate — should be unreachable now
      // that the config block UI requires all four fields with HTML5
      // required + auto-seeded defaults, but kept as defense-in-depth.
      if (/SLA Support project requires|coverage entry/i.test(msg)) {
        setError(
          'SLA Support contract is incomplete — fill in contract dates, frequency, and at least one coverage entry in the blue box above.',
        );
        scrollFocus('project-form-contract-start');
        return;
      }
      setError((err as Error).message || 'Failed to save project.');
      // Generic top-of-form error — scroll the toast itself into view.
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const updateField = (field: string, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  // MKS-372 — only a project actually MOVING to Time & Material gets the
  // pre-conversion step. A project already on T&M edits its rates through the
  // normal rates table, and no other payment type reads role rates at all.
  const isConvertingToTimeAndMaterial =
    isEditing &&
    project != null &&
    trio.paymentTypeId === TIME_AND_MATERIAL_PAYMENT_TYPE_ID &&
    (project.paymentTypeId ?? null) !== TIME_AND_MATERIAL_PAYMENT_TYPE_ID;
  const showTmConversionStep =
    tmConversionOn && isConvertingToTimeAndMaterial && projectId != null;

  return (
    <div
      className={`${showEngineering && tab === 'engineering' ? 'max-w-7xl' : 'max-w-2xl'} mx-auto space-y-6`}
    >
      {/* MKS-251 — confirmation before a type change on an existing project.
          It replaces the migration wizard's "type the project name" gate: the
          point is that the user understands the consequence, not that they can
          copy a string. */}
      <Modal
        isOpen={pendingTypeChange !== null}
        onClose={() => setPendingTypeChange(null)}
        title="Change this project's types?"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setPendingTypeChange(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              // MKS-372 — a definite `false` from the readiness check blocks
              // the save; `null` (still loading, or the flag is off) does not.
              disabled={isPending || (showTmConversionStep && tmConversionReady === false)}
              onClick={() => {
                setPendingTypeChange(null);
                void submitForm();
              }}
            >
              {isPending ? 'Saving...' : 'Change and save'}
            </Button>
          </div>
        }
      >
        <ul className="list-disc pl-5 space-y-1 text-sm text-gray-900">
          {(pendingTypeChange ?? []).map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        <p className="mt-3 text-sm text-gray-600">
          Months that are already invoiced keep their issued amounts, and approved
          hours are not touched. From the current month onwards the project is
          invoiced under the new settings.
        </p>
        {/* MKS-372 — the pre-conversion step. It EXTENDS this confirmation; the
            lines above and the note about invoiced months stay as they were. */}
        {showTmConversionStep && (
          <TmConversionStep
            projectId={projectId!}
            onReadyChange={setTmConversionReady}
            onSeeded={(rates) => {
              // The rates that now exist on the project are the ones the save
              // must send: the T&M assign path replaces the rate table
              // wholesale, so sending the form's stale rows would undo the
              // seed in the very next request.
              setOverageRates(rates);
              setTmConversionIssues(null);
            }}
          />
        )}
      </Modal>

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">
          {isEditing ? 'Edit Project' : 'New Project'}
        </h1>
        <Button variant="ghost" onClick={handleCancel}>
          Cancel
        </Button>
      </div>

      {showEngineering && projectTabBar}

      {/* D-20 — General stays mounted (hidden) while Engineering is shown, so unsaved edits
          survive a tab switch. Without the Engineering tab this wrapper is always visible. */}
      <div hidden={showEngineering && tab === 'engineering'} className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-800">{error}</p>
          {/* MKS-372 — the items the backend refused the conversion over. */}
          {tmConversionIssues && tmConversionIssues.length > 0 && (
            <ul className="mt-2 list-disc pl-5 space-y-1 text-sm text-red-800">
              {tmConversionIssues.map((issue, idx) => (
                <li key={`${issue.type}-${issue.roleId ?? 'none'}-${idx}`}>
                  {issue.message}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* MKS-251 — this used to be an amber "use the Migration Wizard" banner
          on projects whose template trio was still null. The wizard is gone and
          the selectors below are editable, so the banner only has something to
          say when the current user is not allowed to touch them. */}
      {isEditing && !canEditTypes && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-amber-900 mb-1">
            Payment, project, and task type are read-only here
          </h3>
          <p className="text-sm text-amber-800">{typeSelectorDisabledReason}</p>
        </div>
      )}

      {/* noValidate: the app is English-only, but native HTML5 validation bubbles
          render in the browser's locale (e.g. Czech). Suppress them and rely on the
          English inline guards (setError) + English backend validation (catch block). */}
      <form onSubmit={handleSubmit} noValidate className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => updateField('name', e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Project name"
            required
          />
        </div>

        {/* Phase 27 D-13/D-14: Slug input. Editable on create; read-only on edit.
            UAT 2026-05-13: Suggest button fetches a free slug from the backend
            (reuses Phase 27 allocateSlug). Respects D-01 — soft-deleted
            projects still hold their slug — so Suggest correctly avoids
            slugs that would conflict at INSERT time. */}
        <div>
          <label htmlFor="project-form-slug" className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
          <div className="flex items-center gap-2">
            <input
              id="project-form-slug"
              type="text"
              value={form.slug}
              onChange={(e) => {
                updateField('slug', e.target.value);
                setSlugError(null);
              }}
              onBlur={(e) => updateField('slug', e.target.value.toUpperCase())}
              className={`w-32 rounded-lg border px-3 py-2 text-sm font-mono uppercase tracking-wide focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed ${
                slugError ? 'border-red-400' : 'border-gray-300'
              }`}
              placeholder="e.g. INT"
              maxLength={3}
              pattern="[A-Za-z0-9]{3}"
              disabled={isEditing}
              readOnly={isEditing}
            />
            {!isEditing && (
              <button
                type="button"
                disabled={suggestingSlug || !form.name.trim()}
                onClick={async () => {
                  if (!form.name.trim()) return;
                  setSuggestingSlug(true);
                  setSlugError(null);
                  try {
                    const suggested = await suggestProjectSlug(form.name.trim());
                    updateField('slug', suggested);
                  } catch (e) {
                    setSlugError(
                      (e as Error).message ||
                        'Could not suggest a slug — try typing one manually.',
                    );
                  } finally {
                    setSuggestingSlug(false);
                  }
                }}
                className="inline-flex items-center px-3 py-2 text-xs font-medium text-blue-700 bg-white border border-blue-300 rounded-lg hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title={
                  !form.name.trim()
                    ? 'Type a project name first'
                    : 'Find a free 3-character slug derived from the name'
                }
              >
                {suggestingSlug ? 'Suggesting…' : 'Suggest'}
              </button>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-500">
            {isEditing
              ? 'Slug is fixed at project creation and cannot be changed.'
              : '3 letters/digits — leave blank to auto-derive from name, or click Suggest for a guaranteed-free slug.'}
          </p>
          {slugError && <p className="mt-1 text-xs text-red-600">{slugError}</p>}
        </div>

        {/* Full Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
          <input
            type="text"
            value={form.fullName}
            onChange={(e) => updateField('fullName', e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Full project name (optional)"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            value={form.description}
            onChange={(e) => updateField('description', e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Project description (optional)"
          />
        </div>

        {/* Parent Project */}
        <div>
          <label htmlFor="project-form-parent" className="block text-sm font-medium text-gray-700 mb-1">Parent Project</label>
          <div className="flex gap-2">
            <select
              id="project-form-parent"
              value={form.parentId}
              onChange={(e) => updateField('parentId', e.target.value)}
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">No parent (top-level project)</option>
              {allProjects
                ?.filter((p) => p.id !== projectId && p.state !== 'archived' && p.state !== 'cancelled' && p.state !== 'completed')
                .sort((a, b) => (a.fullName || a.name).localeCompare(b.fullName || b.name))
                .map((p) => (
                  <option key={p.id} value={String(p.id)}>
                    {p.fullName || p.name}
                  </option>
                ))}
            </select>
            {form.parentId && (
              <button
                type="button"
                onClick={() => updateField('parentId', '')}
                className="p-2 text-gray-400 hover:text-red-500 rounded-lg border border-gray-300 hover:border-red-300 transition-colors"
                title="Remove parent project"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {/* Customer */}
        <div ref={customerDropdownRef}>
          <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
          <div className="relative">
            <input
              ref={customerInputRef}
              type="text"
              value={isCustomerDropdownOpen
                ? customerFilter
                : customers?.find((c) => String(c.id) === form.customerId)?.name ?? ''}
              onChange={(e) => {
                setCustomerFilter(e.target.value);
                if (!isCustomerDropdownOpen) setIsCustomerDropdownOpen(true);
              }}
              onFocus={() => {
                setCustomerFilter('');
                setIsCustomerDropdownOpen(true);
              }}
              placeholder="Select or create a customer..."
              className="w-full rounded-lg border border-gray-300 px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <button
              type="button"
              onClick={() => setIsCustomerDropdownOpen(!isCustomerDropdownOpen)}
              aria-label={isCustomerDropdownOpen ? 'Close customer dropdown' : 'Open customer dropdown'}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600"
            >
              <ChevronDownIcon className={`w-4 h-4 transition-transform ${isCustomerDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {isCustomerDropdownOpen && (() => {
              const allCustomers = customers ?? [];
              const filtered = allCustomers.filter((c) =>
                c.name.toLowerCase().includes(customerFilter.toLowerCase())
              );
              const isNew = customerFilter.trim() !== '' &&
                !allCustomers.some((c) => c.name.toLowerCase() === customerFilter.trim().toLowerCase());

              return (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-auto">
                  {!customerFilter.trim() && (
                    <button
                      type="button"
                      onClick={() => {
                        updateField('customerId', '');
                        setCustomerFilter('');
                        setIsCustomerDropdownOpen(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-blue-50 ${
                        !form.customerId ? 'bg-blue-50 text-blue-700' : 'text-gray-500'
                      }`}
                    >
                      No customer
                    </button>
                  )}
                  {filtered.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => {
                        updateField('customerId', String(c.id));
                        setCustomerFilter('');
                        setIsCustomerDropdownOpen(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-blue-50 ${
                        String(c.id) === form.customerId ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                    >
                      {c.name}
                    </button>
                  ))}
                  {isNew && (
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const newCustomer = await createCustomerMutation.mutateAsync(customerFilter.trim());
                          updateField('customerId', String(newCustomer.id));
                          setCustomerFilter('');
                          setIsCustomerDropdownOpen(false);
                        } catch {
                          // mutation error handled by React Query
                        }
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-green-50 text-green-700 border-t border-gray-100 flex items-center gap-2"
                    >
                      <PlusIcon className="w-4 h-4" />
                      Add &ldquo;{customerFilter.trim()}&rdquo;
                    </button>
                  )}
                </div>
              );
            })()}
          </div>
        </div>

        {/* Project Manager */}
        <div>
          <label htmlFor="project-form-manager" className="block text-sm font-medium text-gray-700 mb-1">Project Manager</label>
          <select
            id="project-form-manager"
            value={form.projectManagerId}
            onChange={(e) => updateField('projectManagerId', e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">No project manager</option>
            {users?.map((u) => (
              <option key={u.id} value={String(u.id)}>
                {[u.lastName, u.firstName].filter(Boolean).join(' ') || u.email}
              </option>
            ))}
          </select>
        </div>

        {/* Dates — Phase 68 (Plan 68-06 / D-10 / R8): required for all payment types */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="project-form-start-date" className="block text-sm font-medium text-gray-700 mb-1">
              Start Date <span className="text-red-500">*</span>
            </label>
            <DateField
              id="project-form-start-date"
              value={form.startDate}
              onChange={(nextDate) => updateField('startDate', nextDate)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              required={!isEditing}
            />
          </div>
          <div>
            <label htmlFor="project-form-end-date" className="block text-sm font-medium text-gray-700 mb-1">
              End Date <span className="text-red-500">*</span>
            </label>
            <DateField
              id="project-form-end-date"
              value={form.endDate}
              onChange={(nextDate) => updateField('endDate', nextDate)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              required={!isEditing}
            />
          </div>
        </div>

        {/* Default project flag */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="isDefault"
            checked={form.isDefault}
            onChange={(e) => updateField('isDefault', e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label htmlFor="isDefault" className="text-sm text-gray-700">
            Default project (visible to all users for time tracking)
          </label>
        </div>

        {/* Mandatory tasks flag */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="mandatoryTasks"
            checked={form.mandatoryTasks}
            onChange={(e) => updateField('mandatoryTasks', e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label htmlFor="mandatoryTasks" className="text-sm text-gray-700">
            Mandatory tasks (require task selection for time entries)
          </label>
        </div>

        {/* Allow external intake flag — Phase 51 INTAKE-01 (D-03) */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="allowExternalIntake"
            checked={form.allowExternalIntake}
            onChange={(e) => updateField('allowExternalIntake', e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label htmlFor="allowExternalIntake" className="text-sm text-gray-700">
            Allow external intake (triage)
          </label>
          <span className="text-xs text-gray-500">
            External clients can submit tasks into the Triage column (default off).
          </span>
        </div>

        {/* Open board to all internal users flag — 260727-gob */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="openToAllInternal"
            checked={form.openToAllInternal}
            onChange={(e) => updateField('openToAllInternal', e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label htmlFor="openToAllInternal" className="text-sm text-gray-700">
            Open board to all employees
          </label>
          <span className="text-xs text-gray-500">
            Any internal user can view the board and report tasks/comments without being a project member (default on).
          </span>
        </div>

        {/* Billable flag — Phase 62 (Plan 62-06, TBI-01, D-01) */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="billable"
            checked={form.billable ?? true}
            onChange={(e) => updateField('billable', e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label htmlFor="billable" className="text-sm text-gray-700">
            Billable project (appears in To Be Invoiced queue)
          </label>
        </div>

        {/* SLA Support project flag — MKS-264. The replacement for the Project
            type dropdown: ticked is projectTypeId 2 (SLA Support), unticked is
            1 (Default), and everything downstream (the SLA Support settings
            card below, the contract + coverage auto-seed, the MKS-251
            type-change confirmation, the submit payload) already reads
            `trio.projectTypeId`, so the control is the only thing that
            changes. Payment = SLA balíček still ticks it through the
            recommendedProjectTypeId auto-sync; the user can untick it.
            Disabled for the same audience the type dropdowns are. */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="slaSupportProject"
            checked={trio.projectTypeId === SLA_SUPPORT_PROJECT_TYPE_ID}
            disabled={typeSelectorDisabled}
            onChange={(e) =>
              setTrio((prev) => ({
                ...prev,
                projectTypeId: e.target.checked
                  ? SLA_SUPPORT_PROJECT_TYPE_ID
                  : BASELINE_TEMPLATE_ID,
              }))
            }
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
          />
          <label htmlFor="slaSupportProject" className="text-sm text-gray-700">
            SLA Support project
          </label>
          <span className="text-xs text-gray-500">
            Adds the SLA Support contract and coverage settings (default off).
          </span>
        </div>

        {/* Disable time reporting — F3M-01 */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="timeReportingDisabled"
            checked={form.timeReportingDisabled ?? false}
            onChange={(e) => updateField('timeReportingDisabled', e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label htmlFor="timeReportingDisabled" className="text-sm text-gray-700">
            Disable time reporting (hours cannot be logged on this project)
          </label>
        </div>

        {/* Task staleness indicator — 260913-jpy, opt-in per project, default OFF */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="stalenessIndicatorEnabled"
            checked={form.stalenessIndicatorEnabled}
            onChange={(e) => updateField('stalenessIndicatorEnabled', e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label htmlFor="stalenessIndicatorEnabled" className="text-sm text-gray-700">
            Show stale-task emoji on the board
          </label>
          <span className="text-xs text-gray-500">
            Flags tasks with no recent activity on Board view, List view, and the task detail
            panel (default off).
          </span>
        </div>
        {form.stalenessIndicatorEnabled && (
          <div className="grid grid-cols-2 gap-4 ml-6">
            <div>
              <label htmlFor="stalenessStaleDays" className="block text-sm font-medium text-gray-700 mb-1">
                Stale after (days)
              </label>
              <input
                type="number"
                id="stalenessStaleDays"
                min={1}
                max={365}
                step={1}
                value={form.stalenessStaleDays}
                onChange={(e) => updateField('stalenessStaleDays', e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="stalenessGraveyardDays" className="block text-sm font-medium text-gray-700 mb-1">
                Abandoned after (days)
              </label>
              <input
                type="number"
                id="stalenessGraveyardDays"
                min={1}
                max={365}
                step={1}
                value={form.stalenessGraveyardDays}
                onChange={(e) => updateField('stalenessGraveyardDays', e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
          </div>
        )}

        {/* Labels */}
        {pickableLabels.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Labels</label>
            {billabilityLabelsReadOnly && (
              <p className="mb-2 text-xs text-gray-500">
                Billability:{' '}
                <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                  {billabilityLabelNameFor(form.billable ?? true)}
                </span>{' '}
                — set automatically from the Billable checkbox above.
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              {pickableLabels.map((label) => {
                const isSelected = selectedLabelIds.includes(label.id);
                return (
                  <button
                    key={label.id}
                    type="button"
                    onClick={() => {
                      if (isSelected) {
                        setSelectedLabelIds(selectedLabelIds.filter((id) => id !== label.id));
                      } else {
                        setSelectedLabelIds([...selectedLabelIds, label.id]);
                      }
                    }}
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      isSelected
                        ? 'ring-2 ring-offset-1'
                        : 'opacity-60 hover:opacity-100'
                    }`}
                    style={{
                      ...labelChipStyle(label.color),
                      ...(isSelected && label.color ? { ringColor: label.color } : {}),
                    }}
                  >
                    {label.name}
                    {isSelected && (
                      <svg className="ml-1.5 h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
            <p className="mt-1 text-xs text-gray-500">Click labels to add or remove them from this project</p>
          </div>
        )}

        {/* Phase 41 Plan 41-10 (Surface 1) — template trio split into two
            passes per UAT 2026-05-13: Payment alone first → conditional
            settings block (Default / SLA Balíček) → Project type + Task
            template. The trio recommendation banner sits above the first
            selector since payment is the canonical anchor. MKS-264 drops
            Project type out of the first pass — it is the SLA Support
            checkbox in the flags list above now. */}
        <div>
          {!bannerDismissed && (
            <TrioRecommendationBanner
              anchor={lastAnchor}
              current={trio}
              onApply={(next) => {
                setTrio(next);
                setBannerDismissed(true);
              }}
              onDismiss={() => setBannerDismissed(true)}
            />
          )}
          <ProjectFormTemplateSelector
            value={trio}
            onChange={(next, anchor) => {
              // MKS-261 — a hand-picked payment type outranks the lead prefill.
              paymentTypePickedRef.current = true;
              setTrio(next);
              setLastAnchor(anchor);
              setBannerDismissed(false);
            }}
            disabled={typeSelectorDisabled}
            disabledReason={typeSelectorDisabledReason}
            only={['payment']}
            allowedPaymentTypeIds={createModeNarrowingActive ? createPaymentTypeIds : undefined}
          />
        </div>

        {/* Default (or null) PaymentType settings — Default Rate + Budget.
            UAT 2026-05-13: each PaymentType now owns its own blue settings
            block; null treated as Default to match the trio selector's
            BASELINE_TEMPLATE_ID visual fallback. */}
        {(trio.paymentTypeId === null || trio.paymentTypeId === DEFAULT_PAYMENT_TYPE_ID) && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3">
            <div>
              <p className="text-sm font-medium text-blue-900">Default settings</p>
              <p className="text-xs text-blue-700 mt-0.5">
                Hourly rate and total budget for the Default payment template.
                All fields optional.
              </p>
            </div>
            {/* Phase 72 (currency-aware budgets, Plan 04) — the "Default Rate"
                CurrencyRateInput was removed from the UI here: it carried its
                own currency selector + "Convert to CZK", which collided
                visually with the new Budget currency selector below (two
                "Currency" controls looked broken). `form.defaultRate` is left
                untouched in state and still sent on submit so an existing
                project's stored value is preserved (non-destructive — the
                backend column + its retirement are a separate follow-up). */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label
                  htmlFor="project-form-budget-hours"
                  className="block text-xs font-medium text-gray-700 mb-1"
                >
                  Budget (Hours)
                </label>
                <input
                  id="project-form-budget-hours"
                  type="number"
                  step="0.5"
                  min="0"
                  value={form.hoursTotal}
                  onChange={(e) => updateField('hoursTotal', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="0"
                />
              </div>
              {/* Phase 72 (currency-aware budgets, CUR-06) — Budget accepts
                  CZK/EUR/USD and PERSISTS the entered currency (rather than
                  converting-and-discarding it) with a "≈ X CZK (provisional)"
                  preview. */}
              <PersistCurrencyAmountInput
                label="Budget"
                amount={form.moneyTotal}
                currency={form.moneyTotalCurrency}
                onAmountChange={(v) => updateField('moneyTotal', v)}
                onCurrencyChange={(c) => updateField('moneyTotalCurrency', c)}
                placeholder="0.00"
                amountInputId="project-form-budget-czk"
                currencySelectId="project-form-budget-currency"
              />
            </div>
          </div>
        )}

        {/* Phase 37 (Plan 37-08) — SLA balíček satellite fields.
            Service-layer gate requires balicekHours, balicekAmount, and
            balicekCurrency when paymentTypeId === SLA_BALICEK_PAYMENT_TYPE_ID.
            Create path only — PATCH /:id/templates is a separate, not-yet-wired
            mutation owned by a future phase. */}
        {/* UAT 2026-05-13 F-D6/Issue-B — SLA Balíček settings block now
            shows on both create AND edit. On edit the form state is hydrated
            from `project.balicek*` columns in the useEffect below. */}
        {trio.paymentTypeId === SLA_BALICEK_PAYMENT_TYPE_ID && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3">
            <div>
              <p className="text-sm font-medium text-blue-900">SLA Balíček settings</p>
              <p className="text-xs text-blue-700 mt-0.5">
                Required for the SLA Balíček payment template — monthly hours,
                amount, and billing currency.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label
                  htmlFor="project-form-balicek-hours"
                  className="block text-xs font-medium text-gray-700 mb-1"
                >
                  Hours / month <span className="text-red-500">*</span>
                </label>
                <input
                  id="project-form-balicek-hours"
                  type="number"
                  min={1}
                  step={1}
                  value={form.balicekHours}
                  onChange={(e) => updateField('balicekHours', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g. 40"
                  required
                />
              </div>
              {/* Phase 72 (currency-aware budgets, CUR-06/CUR-02) — same
                  persist-currency + provisional-preview input as the Default
                  Budget field above. */}
              <PersistCurrencyAmountInput
                label="Amount / month"
                amount={form.balicekAmount}
                currency={form.balicekCurrency as 'CZK' | 'EUR' | 'USD'}
                onAmountChange={(v) => updateField('balicekAmount', v)}
                onCurrencyChange={(c) => updateField('balicekCurrency', c)}
                placeholder="e.g. 5000"
                amountInputId="project-form-balicek-amount"
                currencySelectId="project-form-balicek-currency"
                required
              />
            </div>
            {/* Optional behavior: over-limit policy + alert thresholds.
                Both fields are optional — backend defaults: overLimitAllowed=false,
                alertThresholds=[]. Sent only when the user populates them. */}
            <div className="border-t border-blue-200 pt-3 space-y-3">
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="project-form-balicek-overlimit"
                  checked={form.overLimitAllowed}
                  onChange={(e) => {
                    updateField('overLimitAllowed', e.target.checked);
                    setBalicekError(null);
                  }}
                  className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <div>
                  <label
                    htmlFor="project-form-balicek-overlimit"
                    className="block text-xs font-medium text-blue-900"
                  >
                    Allow over-limit hours
                  </label>
                  <p className="text-xs text-blue-700 mt-0.5">
                    When enabled, work continues after the monthly package is
                    exhausted and is billed via overage rates. When off,
                    dispatch blocks new tasks once the package runs out.
                  </p>
                </div>
              </div>
              {balicekError && (
                <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2">
                  <p className="text-xs text-red-800">{balicekError}</p>
                </div>
              )}
              {/* Overage rates — per-role hourly rate for hours beyond the
                  monthly package. Required (≥1) when overLimit is enabled
                  (PAY-03 backend gate). Hidden when overLimit is off. */}
              {form.overLimitAllowed && (
                <div className="space-y-2">
                  <div>
                    <label className="block text-xs font-medium text-blue-900 mb-1">
                      Overage rates <span className="text-red-500">*</span>
                    </label>
                    <p className="text-xs text-blue-700">
                      Hourly rate billed per role for hours beyond the monthly
                      package. Currency follows the package currency above.
                    </p>
                  </div>
                  {overageRates.map((row, idx) => (
                    <div key={idx} className="flex items-end gap-2">
                      <div className="flex-1">
                        <label className="block text-xs text-gray-600 mb-0.5">Role</label>
                        <select
                          value={row.roleId}
                          onChange={(e) =>
                            setOverageRates((prev) =>
                              prev.map((r, i) => (i === idx ? { ...r, roleId: e.target.value } : r))
                            )
                          }
                          className="w-full rounded-lg border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                          required
                        >
                          <option value="">Select role…</option>
                          {projectRoles.map((role) => (
                            <option key={role.id} value={String(role.id)}>
                              {role.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="w-32">
                        <label className="block text-xs text-gray-600 mb-0.5">
                          Hourly rate ({form.balicekCurrency})
                        </label>
                        <input
                          type="text"
                          inputMode="decimal"
                          pattern="^[0-9]+(\.[0-9]{1,2})?$"
                          value={row.hourlyRate}
                          onChange={(e) =>
                            setOverageRates((prev) =>
                              prev.map((r, i) =>
                                i === idx ? { ...r, hourlyRate: e.target.value } : r
                              )
                            )
                          }
                          className="w-full rounded-lg border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="50.00"
                          required
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setOverageRates((prev) => prev.filter((_, i) => i !== idx))
                        }
                        className="mb-1 text-gray-400 hover:text-red-500 p-1"
                        title="Remove rate"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() =>
                      setOverageRates((prev) => [...prev, { roleId: '', hourlyRate: '' }])
                    }
                    className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-blue-700 hover:text-blue-900 bg-white border border-blue-300 rounded-lg hover:bg-blue-50"
                  >
                    <PlusIcon className="w-3 h-3" />
                    Add overage rate
                  </button>
                </div>
              )}
              <div>
                <label
                  htmlFor="project-form-balicek-thresholds"
                  className="block text-xs font-medium text-blue-900 mb-1"
                >
                  Alert thresholds (%)
                  {form.overLimitAllowed && <span className="text-red-500"> *</span>}
                </label>
                <input
                  id="project-form-balicek-thresholds"
                  type="text"
                  inputMode="numeric"
                  value={form.alertThresholds}
                  onChange={(e) => {
                    updateField('alertThresholds', e.target.value);
                    setBalicekError(null);
                  }}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="80, 100, 120"
                  required={form.overLimitAllowed}
                />
                <p className="text-xs text-blue-700 mt-1">
                  Comma-separated percentages (1–200) of monthly consumption
                  that trigger notifications.{' '}
                  {form.overLimitAllowed
                    ? 'Required when over-limit is enabled.'
                    : 'Leave blank for no alerts.'}
                </p>
              </div>
            </div>

            {/* Phase 53 — Rollover policy (D-04, D-05) */}
            <div className="border-t border-blue-200 pt-3 space-y-3">
              <p className="text-xs font-semibold text-blue-900 mt-1">Rollover policy</p>
              <p className="text-xs text-blue-700 mt-1">
                Unused package hours can carry forward instead of expiring at month end.
              </p>
              <div>
                <label
                  htmlFor="project-form-rollover-policy"
                  className="block text-xs font-medium text-gray-700 mb-1"
                >
                  Rollover policy
                </label>
                <select
                  id="project-form-rollover-policy"
                  value={form.rolloverPolicy}
                  onChange={(e) =>
                    updateField('rolloverPolicy', e.target.value as 'none' | 'monthly' | 'annual')
                  }
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="none">No rollover (hours expire)</option>
                  <option value="monthly">Monthly (previous month carries)</option>
                  <option value="annual">Annual (accumulates within calendar year, resets 1 Jan)</option>
                </select>
              </div>
              {form.rolloverPolicy !== 'none' && (
                <div>
                  <label
                    htmlFor="project-form-rollover-cap"
                    className="block text-xs font-medium text-gray-700 mb-1"
                  >
                    Rollover cap (hours)
                  </label>
                  <input
                    id="project-form-rollover-cap"
                    type="number"
                    min={0}
                    step={1}
                    value={form.rolloverCapHours}
                    onChange={(e) => updateField('rolloverCapHours', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="e.g. 20"
                    aria-describedby="project-form-rollover-cap-hint"
                  />
                  <p id="project-form-rollover-cap-hint" className="text-xs text-blue-700 mt-1">
                    Max hours that may carry per month. Leave blank for no cap.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Phase 58 (PAY-FIX-02, D-03) — Fixed Price settings block.
            Shows when paymentTypeId === FIXED_PRICE_PAYMENT_TYPE_ID.
            Contract amount and currency are required; contract start/end reuse
            existing form state (contractStart / contractEnd). */}
        {trio.paymentTypeId === FIXED_PRICE_PAYMENT_TYPE_ID && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 space-y-3">
            <div>
              <p className="text-sm font-semibold text-emerald-900">Fixed Price settings</p>
              <p className="text-xs text-emerald-700 mt-0.5">
                Required — contract amount and billing currency. This project is excluded from
                automatic invoicing; admin bills the contract amount manually.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {/* Phase 72 (currency-aware budgets, CUR-06/CUR-02) — same
                  persist-currency + provisional-preview input as the Default
                  Budget field above; `allowUnset` keeps the pre-existing
                  "Select currency…" placeholder so an explicit pick is
                  still required (no silent CZK default on create). */}
              <PersistCurrencyAmountInput
                label="Contract amount"
                amount={contractAmount}
                currency={contractCurrency as 'CZK' | 'EUR' | 'USD' | ''}
                onAmountChange={setContractAmount}
                onCurrencyChange={setContractCurrency}
                placeholder="e.g. 50000"
                amountInputId="project-form-contract-amount"
                currencySelectId="project-form-contract-currency"
                required
                allowUnset
              />
            </div>
            {/* MKS-205 — consumption alerts measured against the contract amount. */}
            <div className="border-t border-emerald-200 pt-3">
              <BudgetAlertThresholdsField
                value={form.alertThresholds}
                onChange={(next) => updateField('alertThresholds', next)}
                hasBudget={hasBudgetForAlerts}
                inputId="project-form-fixed-price-thresholds"
                textClass="text-emerald-700"
              />
            </div>
            {balicekError && trio.paymentTypeId === FIXED_PRICE_PAYMENT_TYPE_ID && (
              <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2">
                <p className="text-xs text-red-800">{balicekError}</p>
              </div>
            )}
          </div>
        )}

        {/* Phase 58 (PAY-TM-02, D-03) — Time & Material settings block.
            Shows when paymentTypeId === TIME_AND_MATERIAL_PAYMENT_TYPE_ID.
            Reuses the existing overageRates state (shared with SLA Balíček).
            The overage rate rows JSX is copied from the SLA Balíček block
            verbatim per UI-SPEC "Strict reuse mandate". */}
        {trio.paymentTypeId === TIME_AND_MATERIAL_PAYMENT_TYPE_ID && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3">
            <div>
              <p className="text-sm font-semibold text-blue-900">Time & Material settings</p>
              <p className="text-xs text-blue-700 mt-0.5">
                {tmRatesRequired
                  ? 'Per-role hourly rates for Time & Material billing. At least one rate is required.'
                  : 'This project is non-billable, so rates are optional. You can still pick roles; a rate left empty is saved as 0 (hours are not priced).'}
              </p>
            </div>
            {/* Phase 72 Plan 06 (CUR-09, D-07) — single project-level T&M
                billing currency. Per-role rates below are entered in this
                currency and converted to CZK at invoice-generate time via
                the ČNB fixing (CZK short-circuits with no conversion). */}
            <div className="max-w-xs">
              <label
                htmlFor="project-form-tm-billing-currency"
                className="block text-xs font-medium text-gray-700 mb-1"
              >
                Billing currency
              </label>
              <select
                id="project-form-tm-billing-currency"
                value={form.tmBillingCurrency}
                onChange={(e) =>
                  updateField(
                    'tmBillingCurrency',
                    e.target.value as 'CZK' | 'EUR' | 'USD',
                  )
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="CZK">CZK</option>
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
              </select>
              <p className="text-xs text-blue-700 mt-1">
                Per-role rates below are entered in this currency and billed in CZK at the
                ČNB rate on the invoice date.
              </p>
            </div>
            <div id="project-form-tm-rates" className="space-y-2">
              {overageRates.map((row, idx) => (
                <div key={idx} className="flex items-end gap-2">
                  <div className="flex-1">
                    <label className="block text-xs text-gray-600 mb-0.5">Role</label>
                    <select
                      value={row.roleId}
                      onChange={(e) =>
                        setOverageRates((prev) =>
                          prev.map((r, i) => (i === idx ? { ...r, roleId: e.target.value } : r))
                        )
                      }
                      className="w-full rounded-lg border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required={tmRatesRequired}
                    >
                      <option value="">Select role…</option>
                      {projectRoles.map((role) => (
                        <option key={role.id} value={String(role.id)}>
                          {role.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="w-32">
                    <label className="block text-xs text-gray-600 mb-0.5">
                      {tmRatesRequired ? 'Hourly rate' : 'Hourly rate (optional)'}
                    </label>
                    <input
                      type="text"
                      inputMode="decimal"
                      pattern="^[0-9]+(\.[0-9]{1,2})?$"
                      value={row.hourlyRate}
                      onChange={(e) =>
                        setOverageRates((prev) =>
                          prev.map((r, i) =>
                            i === idx ? { ...r, hourlyRate: e.target.value } : r
                          )
                        )
                      }
                      className="w-full rounded-lg border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder={tmRatesRequired ? '50.00' : 'optional'}
                      required={tmRatesRequired}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setOverageRates((prev) => prev.filter((_, i) => i !== idx))
                    }
                    className="mb-1 text-gray-400 hover:text-red-500 p-1"
                    aria-label="Remove rate row"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() =>
                  setOverageRates((prev) => [...prev, { roleId: '', hourlyRate: '' }])
                }
                className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-blue-700 hover:text-blue-900 bg-white border border-blue-300 rounded-lg hover:bg-blue-50"
              >
                <PlusIcon className="w-3 h-3" />
                Add rate
              </button>
            </div>
            {/* MKS-263 — the estimate / maximum promised to the client, stored
                in the same moneyTotal pair the Default Budget writes (one
                budget column, so no second budget-like input anywhere on the
                form: the Default block that owns the other one only renders
                for the Default payment type). It is what the alerts below
                measure against — hence the shared divider. */}
            <div className="border-t border-blue-200 pt-3 space-y-2">
              <div className="grid grid-cols-2 gap-3">
                <PersistCurrencyAmountInput
                  label="Estimated maximum"
                  amount={form.moneyTotal}
                  currency={form.moneyTotalCurrency}
                  onAmountChange={(v) => updateField('moneyTotal', v)}
                  onCurrencyChange={(c) => updateField('moneyTotalCurrency', c)}
                  placeholder="e.g. 50000"
                  amountInputId="project-form-tm-estimated-maximum"
                  currencySelectId="project-form-tm-estimated-maximum-currency"
                />
              </div>
              <p className="text-xs text-blue-700">
                Optional — the estimate, or the maximum promised to the client. It does not
                cap invoicing; hours are still billed as they are booked.
              </p>
            </div>
            {/* MKS-205 — consumption alerts measured against the money budget
                (or the contract amount when one is set). */}
            <BudgetAlertThresholdsField
              value={form.alertThresholds}
              onChange={(next) => updateField('alertThresholds', next)}
              hasBudget={hasBudgetForAlerts}
              inputId="project-form-tm-thresholds"
              textClass="text-blue-700"
              emptyHint="Not available until this project has a budget. Fill Estimated maximum above (or set a contract amount) and consumption alerts can be configured here."
            />
            {balicekError && trio.paymentTypeId === TIME_AND_MATERIAL_PAYMENT_TYPE_ID && (
              <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2">
                <p className="text-xs text-red-800">{balicekError}</p>
              </div>
            )}
          </div>
        )}

        {/* Phase 36 (Plan 36-05) — SLA Support contract + coverage entries.
            Service-layer gate requires contractStart/End/Frequency AND ≥1
            coverage entry when projectTypeId === SLA_SUPPORT_PROJECT_TYPE_ID.
            Defaults auto-seeded on first SLA Support land; admin can override
            every field. ProjectType is hidden on create (auto-synced from
            Payment), but this block surfaces the contract requirements that
            the auto-sync silently inherits. */}
        {!isEditing && trio.projectTypeId === SLA_SUPPORT_PROJECT_TYPE_ID && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3">
            <div>
              <p className="text-sm font-medium text-blue-900">SLA Support settings — Contract & coverage</p>
              <p className="text-xs text-blue-700 mt-0.5">
                Required for the SLA Support project type — billing frequency and at
                least one coverage schedule entry. Contract dates use the project
                start/end entered above.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label
                  htmlFor="project-form-contract-frequency"
                  className="block text-xs font-medium text-gray-700 mb-1"
                >
                  Frequency <span className="text-red-500">*</span>
                </label>
                <select
                  id="project-form-contract-frequency"
                  value={form.contractFrequency}
                  onChange={(e) => updateField('contractFrequency', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                  <option value="one_off">One-off</option>
                  <option value="perpetual">Perpetual</option>
                </select>
              </div>
            </div>
            {/* Petra UAT 2026-05-26 — coverage_entries timezone/preset/holiday-country
                UI removed from create form. Phase 47 (D-07) flipped slaActiveWindowService
                from coverage_entries to coverage_cells, so entries are now vestigial for
                SLA window logic; a single default entry (Europe/Prague + business_hours +
                CZ) is silently auto-seeded by the useEffect at line ~351 to satisfy the
                Phase 36 backend gate. User configures real coverage (per-team cells + per-cell
                billing rate for internal cost tracking) on the dedicated /coverage page
                after project create. */}
            <div className="border-t border-blue-200 pt-3 space-y-2">
              <p className="text-xs font-medium text-blue-900">
                Coverage schedule
              </p>
              <p className="text-xs text-blue-700">
                The full coverage schedule (per-team cells + optional per-cell
                billing rate) is configured on the dedicated Coverage editor
                after the project is created. Per-cell rates are tracked
                <span className="font-medium"> for internal cost optimization only</span> —
                client invoicing follows the Payment template (SLA Balíček monthly fee).
              </p>
            </div>
          </div>
        )}

        {/* Petra UAT 2026-05-20 (test C) — on EDIT, contract metadata + coverage
            entries are NOT editable inline (decision: ProjectFormPage doesn't
            own SLA Support edit surface — dedicated /projects/:id/coverage
            page does). Surface a prominent link so admin doesn't think the
            settings vanished. */}
        {isEditing && project && project.projectTypeId === SLA_SUPPORT_PROJECT_TYPE_ID && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-2">
            <div>
              <p className="text-sm font-medium text-blue-900">
                SLA Support settings — Contract &amp; coverage
              </p>
              <p className="text-xs text-blue-700 mt-0.5">
                Contract dates, billing frequency, and coverage entries are
                edited on the dedicated coverage page.
              </p>
            </div>
            <Link
              to={`/projects/${project.id}/coverage`}
              className="inline-flex items-center gap-1 text-sm font-medium text-blue-700 hover:text-blue-900 hover:underline"
            >
              Open coverage editor &rarr;
            </Link>
          </div>
        )}

        {/* Task template — second pass of the trio selector. Petra UAT
            2026-05-26 moved Project type up to the first pass (alongside
            Payment) so the SLA Support settings card renders BELOW its
            controlling dropdown, not above it. Auto-sync from
            PaymentType.recommendedProjectTypeId still applies via the
            useEffect at line ~332 as a sensible default after Payment
            selection — user can override. Lock banner suppressed (already
            shown by the first pass). */}
        <ProjectFormTemplateSelector
          value={trio}
          onChange={(next, anchor) => {
            setTrio(next);
            setLastAnchor(anchor);
            setBannerDismissed(false);
          }}
          disabled={typeSelectorDisabled}
          only={['task']}
          showLockBanner={false}
        />

        {/* Phase 38 (Plan 38-05) — SLA Task priority tiers. Service-layer
            gate requires ≥1 tier when taskTypeId === SLA_TASK_TYPE_ID.
            Create path only — admin > SLA Tiers page owns post-create CRUD. */}
        {!isEditing && trio.taskTypeId === SLA_TASK_TYPE_ID && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3">
            <div>
              <p className="text-sm font-medium text-blue-900">SLA Task settings — Priority tiers</p>
              <p className="text-xs text-blue-700 mt-0.5">
                Required for the SLA Task template. At least one tier; each
                tier defines response + resolution targets and percentage
                thresholds that trigger consumption alerts.
              </p>
            </div>
            <div className="space-y-3">
              {slaTiers.map((tier, idx) => (
                <div
                  key={idx}
                  className="rounded-md border border-blue-300 bg-white p-3 space-y-2"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 grid grid-cols-[1fr_auto] gap-2">
                      <input
                        type="text"
                        value={tier.name}
                        onChange={(e) =>
                          setSlaTiers((prev) =>
                            prev.map((t, i) => (i === idx ? { ...t, name: e.target.value } : t))
                          )
                        }
                        className="rounded-lg border border-gray-300 px-2 py-1 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Tier name (e.g. P1 — Critical)"
                        required
                      />
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={tier.orderIndex}
                        onChange={(e) =>
                          setSlaTiers((prev) =>
                            prev.map((t, i) =>
                              i === idx ? { ...t, orderIndex: parseInt(e.target.value, 10) || 1 } : t
                            )
                          )
                        }
                        className="w-16 rounded-lg border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        title="Priority order (1 = highest)"
                      />
                    </div>
                    {slaTiers.length > 1 && (
                      <button
                        type="button"
                        onClick={() =>
                          setSlaTiers((prev) => prev.filter((_, i) => i !== idx))
                        }
                        className="text-gray-400 hover:text-red-500 p-1"
                        title="Remove tier"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-600 mb-0.5">Response (hours)</label>
                      <input
                        type="number"
                        min={0}
                        step={0.5}
                        value={tier.responseHours}
                        onChange={(e) =>
                          setSlaTiers((prev) =>
                            prev.map((t, i) =>
                              i === idx ? { ...t, responseHours: e.target.value } : t
                            )
                          )
                        }
                        className="w-full rounded-lg border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-0.5">Resolution (hours)</label>
                      <input
                        type="number"
                        min={0}
                        step={0.5}
                        value={tier.resolutionHours}
                        onChange={(e) =>
                          setSlaTiers((prev) =>
                            prev.map((t, i) =>
                              i === idx ? { ...t, resolutionHours: e.target.value } : t
                            )
                          )
                        }
                        className="w-full rounded-lg border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-0.5">Response warning (%)</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={tier.responseWarningCsv}
                        onChange={(e) =>
                          setSlaTiers((prev) =>
                            prev.map((t, i) =>
                              i === idx ? { ...t, responseWarningCsv: e.target.value } : t
                            )
                          )
                        }
                        className="w-full rounded-lg border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="50, 80"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-0.5">Resolution warning (%)</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={tier.resolutionWarningCsv}
                        onChange={(e) =>
                          setSlaTiers((prev) =>
                            prev.map((t, i) =>
                              i === idx ? { ...t, resolutionWarningCsv: e.target.value } : t
                            )
                          )
                        }
                        className="w-full rounded-lg border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="50, 80"
                      />
                    </div>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={() =>
                  setSlaTiers((prev) => [
                    ...prev,
                    {
                      name: '',
                      orderIndex: prev.length > 0 ? Math.max(...prev.map((t) => t.orderIndex)) + 1 : 1,
                      responseHours: '4',
                      resolutionHours: '24',
                      responseWarningCsv: '50, 80',
                      resolutionWarningCsv: '50, 80',
                    },
                  ])
                }
                className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-blue-700 hover:text-blue-900 bg-white border border-blue-300 rounded-lg hover:bg-blue-50"
              >
                <PlusIcon className="w-3 h-3" />
                Add tier
              </button>
            </div>
          </div>
        )}

        {/* Petra UAT 2026-05-20 (test C) — on EDIT, SLA priority tiers are
            NOT editable inline (decision: dedicated /admin/sla-tiers page
            owns post-create tier CRUD). Surface a prominent link so admin
            knows where the tier configuration moved. */}
        {isEditing && project && project.taskTypeId === SLA_TASK_TYPE_ID && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-2">
            <div>
              <p className="text-sm font-medium text-blue-900">
                SLA Task settings — Priority tiers
              </p>
              <p className="text-xs text-blue-700 mt-0.5">
                Tier names, response &amp; resolution targets, and warning
                thresholds are edited on the dedicated SLA tiers admin page.
              </p>
            </div>
            <Link
              to={`/admin/sla-tiers?projectId=${project.id}`}
              className="inline-flex items-center gap-1 text-sm font-medium text-blue-700 hover:text-blue-900 hover:underline"
            >
              Manage SLA priority tiers &rarr;
            </Link>
          </div>
        )}

        {/* Board template (Phase 19 — D-01..D-06; create path only).
            UAT 2026-05-13: moved to the very end of the form so the board
            state-template choice is the last decision, after Payment / Project
            type / Task type and all project metadata. */}
        {!isEditing && (
          <div>
            <label htmlFor="project-form-template" className="block text-sm font-medium text-gray-700 mb-1">
              Board template <span className="text-red-500">*</span>
            </label>
            <select
              id="project-form-template"
              value={form.templateId}
              onChange={(e) => updateField('templateId', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
              disabled={!templates}
            >
              {!templates ? (
                <option value="">Loading templates…</option>
              ) : (
                <>
                  {!form.templateId && <option value="">Select a board template…</option>}
                  {templates.map((t) => (
                    <option key={t.id} value={String(t.id)}>
                      {`${t.name} (${t.states.length} states)`}
                    </option>
                  ))}
                </>
              )}
            </select>
            {form.templateId && templates && (() => {
              const selected = templates.find((t) => String(t.id) === form.templateId);
              if (!selected?.description) return null;
              return (
                <p className="mt-1 text-xs text-gray-500">{selected.description}</p>
              );
            })()}
          </div>
        )}

        {/* Submit */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <Button variant="secondary" onClick={handleCancel} type="button">
            Cancel
          </Button>
          <Button type="submit" isLoading={isPending}>
            {isEditing ? 'Save Changes' : 'Create Project'}
          </Button>
        </div>
      </form>

      {/* MKS-94 — billing terms. Outside the form element on purpose: they save
          through their own endpoint (different audience from PATCH /projects/:id),
          so they must not ride along with the main submit. Edit-only — a project
          that does not exist yet has nothing to carry over from a lead. */}
      {isEditing && project && (
        <ProjectBillingTermsSection project={project} canEdit={canEditBillingTerms} />
      )}

      {/* MKS-136 — the billing schedule the terms above drive. Same audience
          and the same reason for living outside the form element: it saves
          through its own endpoints, period by period. */}
      {isEditing && project && (
        <BillingScheduleSection projectId={project.id} canEdit={canEditBillingTerms} />
      )}

      {canEditStates && (
        <section
          className="mt-8 space-y-4 border-t border-gray-100 pt-6"
          data-section="task-states"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Task States</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Most recently applied:{' '}
                <span className="font-medium text-gray-700">
                  {appliedTemplate?.name ?? '—'}
                </span>
              </p>
            </div>
            {isAdmin && activeTemplates.length > 0 && (
              <div className="relative" ref={reapplyDropdownRef}>
                <button
                  type="button"
                  onClick={() => setReapplyDropdownOpen((o) => !o)}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  Re-apply template…
                  <ChevronDownIcon className="h-4 w-4" />
                </button>
                {reapplyDropdownOpen && (
                  <div className="absolute right-0 top-full mt-1 z-40 w-64 rounded-lg border border-gray-200 bg-white shadow-lg">
                    {activeTemplates.map((tpl) => (
                      <button
                        key={tpl.id}
                        type="button"
                        onClick={() => {
                          setReapplyDropdownOpen(false);
                          setReapplyTargetTemplateId(tpl.id);
                        }}
                        className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                      >
                        {tpl.name}
                        <span className="text-xs text-gray-400 ml-2">
                          ({tpl.states.length} states)
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {deleteConflict && (
            <div
              className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 flex items-center gap-3"
              role="alert"
            >
              <span>
                Cannot delete &quot;{deleteConflict.stateName}&quot; — used by{' '}
                {deleteConflict.taskCount} task{deleteConflict.taskCount === 1 ? '' : 's'}.
                Move tasks to another state first.
              </span>
              <Link
                to={`/manager/tasks?projectId=${projectId}&stateId=${deleteConflict.stateId}`}
                className="underline hover:text-red-900"
              >
                View tasks →
              </Link>
              <button
                type="button"
                aria-label="Dismiss"
                onClick={() => setDeleteConflict(null)}
                className="ml-auto text-red-700 hover:text-red-900"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          )}

          <StateListEditor
            states={
              (projectStates ?? []).map((s) => ({
                id: s.id,
                name: s.name,
                ordering: s.ordering,
                color: s.color,
                isTerminal: s.isTerminal,
                isInProgress: s.isInProgress,
              }))
            }
            mode="project"
            onAdd={handleAddState}
            onRename={handleRenameState}
            onRecolor={handleRecolorState}
            onDelete={handleDeleteState}
            onReorder={handleReorderStates}
            onToggleTerminal={handleToggleTerminalState}
            onToggleInProgress={handleToggleInProgressState}
          />

          {/* 260727-czj — opt-in allowlist of roles permitted to move tasks
              into in-progress columns. Empty selection = restriction OFF. */}
          <div className="rounded-lg border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-900">
              Who can move tickets into in-progress columns
            </h3>
            {inProgressAllowedRoleIds.length === 0 ? (
              <p className="text-xs text-gray-500 mt-1">
                No roles selected — the restriction is off and every project member may move
                tickets into in-progress columns.
              </p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">
                Only admins, the project PM, and members with a checked role below may move
                tickets into in-progress columns.
              </p>
            )}
            <div className="mt-3 flex flex-col gap-2">
              {projectRoles.map((role) => (
                <label key={role.id} className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={inProgressAllowedRoleIds.includes(role.id)}
                    onChange={() => handleToggleInProgressAllowedRole(role.id)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  {role.name}
                </label>
              ))}
              {projectRoles.length === 0 && (
                <p className="text-xs text-gray-400">No project roles configured yet.</p>
              )}
            </div>
          </div>

          {reapplyChosenTemplate && projectId && project && (
            <ReapplyTemplateModal
              isOpen={true}
              onClose={() => setReapplyTargetTemplateId(null)}
              projectId={projectId}
              projectName={project.name}
              templateId={reapplyChosenTemplate.id}
              templateName={reapplyChosenTemplate.name}
              projectStates={projectStates ?? []}
              templateStates={reapplyChosenTemplate.states}
              onSuccess={(message) => setTaskStatesToast({ message, variant: 'success' })}
            />
          )}

          <AppToast toast={taskStatesToast} onDismiss={() => setTaskStatesToast(null)} />
        </section>
      )}

      {/* MKS-406 — switch shared (global) task labels off for this project. Flagged inside. */}
      {canEditStates && projectId && <ProjectSharedLabelsSection projectId={projectId} />}
      </div>

      {engineeringMounted && (
        <div hidden={tab !== 'engineering'}>
          <ProjectEngineeringTab projectId={projectId!} />
        </div>
      )}
    </div>
  );
}
